// Données mockup complètes pour le Cadre Référentiel BZ
// Client type : TechnoSolutions Inc. - 75 employés - Services TI & Conseil

export interface Tactique {
  id: string;
  nom: string;
  description: string;
  descriptionDetaillee?: string; // Description holistique enrichie
  pilier: number;
  priorite: 'Haute' | 'Moyenne' | 'Basse';
  statut: 'À faire' | 'En cours' | 'Fait' | 'Optionnel';
  asWas: number; // Score initial (0-5)
  asIs: number; // Score actuel (0-5)
  target: number; // Score cible (0-5)
  raci: {
    responsible: string;
    accountable: string;
    consulted: string;
    informed: string;
  };
  livrables: string[];
  livrablesVisuels?: {
    nom: string;
    type: 'Dashboard' | 'Rapport' | 'Matrice' | 'Présentation' | 'Processus' | 'Outil' | 'Document';
    description: string;
    imageUrl: string;
  }[];
  casUsage?: string; // Exemple concret d'application
  impactMetier?: string; // Impact sur l'organisation
  kpi: string;
  echeance?: string;
}

export interface Pilier {
  id: number;
  nom: string;
  description: string;
  descriptionComplete?: string; // Description holistique enrichie
  objectifStrategique?: string; // Objectif stratégique du pilier
  beneficesCles?: string[]; // Bénéfices clés pour l'organisation
  risquesSiAbsent?: string[]; // Risques si ce pilier est négligé
  icon: string;
  couleur: string;
  asWas: number; // Moyenne des tactiques
  asIs: number;
  target: number;
  nombreTactiques: number;
  tactiquesActives: number;
}

// Client mockup
export const clientMockup = {
  nom: "TechnoSolutions Inc.",
  secteur: "Services TI & Conseil en transformation numérique",
  tailleEntreprise: 75,
  chiffreAffaires: 8500000, // 8.5M$
  budgetTI: 680000, // 8% du CA
  budgetTIParEmploye: 9067, // 680k / 75
  dateEvaluation: "2024-10-15",
  maturiteGlobale: 2.8, // Sur 5
  objectifMaturite: 4.2,
  delaiObjectif: "18 mois",
  contactPrincipal: {
    nom: "Sophie Tremblay",
    titre: "Directrice générale",
    email: "s.tremblay@technosolutions.ca",
    telephone: "(418) 555-0199"
  },
  directeurTI: {
    nom: "Martin Bélanger",
    titre: "Directeur TI (externe - BZ Inc.)",
    email: "m.belanger@bzsolutions.ca"
  }
};

// Les 7 Piliers du Cadre BZ
export const piliers: Pilier[] = [
  {
    id: 1,
    nom: "Stratégie & Gouvernance TI",
    description: "Connecter les TI à la stratégie d'affaires et mesurer la valeur perçue",
    descriptionComplete: "Ce pilier fondamental établit le pont entre la technologie et les objectifs d'affaires. Il garantit que chaque dollar investi en TI crée de la valeur mesurable. Un Directeur TI d'excellence maîtrise l'art de traduire les enjeux d'affaires en solutions technologiques et de démontrer le ROI de chaque initiative. Ce pilier transforme les TI d'un 'centre de coûts' en 'partenaire stratégique créateur de valeur'.",
    objectifStrategique: "Transformer les TI en moteur stratégique de croissance aligné à 100% avec les priorités d'affaires",
    beneficesCles: [
      "Visibilité complète sur comment les TI supportent les revenus et la productivité",
      "Relations de confiance avec la direction basées sur des résultats tangibles",
      "Priorisation intelligente des investissements selon la valeur d'affaires",
      "Budget TI justifié et optimisé (benchmark: 7-10% du CA pour PME services)",
      "Communication bidirectionnelle fluide entre TI et départements métiers"
    ],
    risquesSiAbsent: [
      "TI perçues comme centre de coûts plutôt que créateur de valeur",
      "Déconnexion entre projets TI et priorités stratégiques réelles",
      "Budget TI coupé en période difficile par manque de justification",
      "Frustration des directions internes face à des TI 'sourdes' à leurs besoins",
      "Opportunités d'affaires manquées par lenteur ou inadéquation technologique"
    ],
    icon: "Target",
    couleur: "from-blue-600 to-indigo-600",
    asWas: 1.8,
    asIs: 3.2,
    target: 4.5,
    nombreTactiques: 11,
    tactiquesActives: 8
  },
  {
    id: 2,
    nom: "Gestion des actifs & parties prenantes",
    description: "Maîtriser le 'qui a quoi' et assurer la conformité sécurité",
    descriptionComplete: "La gouvernance commence par la connaissance. Ce pilier constitue votre 'radiographie organisationnelle TI' : savoir précisément qui utilise quoi, depuis combien de temps, à quel coût, avec quels droits d'accès. Une CMDB (Configuration Management Database) bien tenue n'est pas un luxe mais la fondation d'une gestion TI mature. Elle permet optimisation des coûts (licences inutilisées), réduction des risques (accès orphelins), conformité légale (RGPD, Loi 25) et réactivité opérationnelle (remplacements planifiés). Un inventaire à jour est le prérequis de toute décision intelligente.",
    objectifStrategique: "Atteindre une visibilité 360° sur tous les actifs TI et utilisateurs avec conformité sécuritaire totale",
    beneficesCles: [
      "Réduction 15-25% des coûts licences par optimisation usage réel",
      "Conformité légale assurée (Loi 25, RGPD) évitant sanctions financières",
      "Temps résolution incidents réduit de 40% grâce à documentation centralisée",
      "Planification budgétaire précise basée sur cycles de vie réels",
      "Sécurité renforcée par gestion rigoureuse accès et désactivation rapide comptes"
    ],
    risquesSiAbsent: [
      "Failles sécurité critiques : comptes orphelins d'ex-employés avec accès actifs",
      "Gaspillage budgétaire : licences payées mais inutilisées (souvent 20-30% du budget logiciels)",
      "Non-conformité légale : amendes potentielles Loi 25 (jusqu'à 25M$ ou 4% CA global)",
      "Pannes non anticipées : équipements obsolètes jamais remplacés faute de suivi",
      "Chaos opérationnel : impossibilité savoir qui a quoi lors d'incidents majeurs"
    ],
    icon: "Database",
    couleur: "from-emerald-600 to-teal-600",
    asWas: 2.1,
    asIs: 3.5,
    target: 4.3,
    nombreTactiques: 10,
    tactiquesActives: 7
  },
  {
    id: 3,
    nom: "Gestion de projet",
    description: "Transformer les idées en valeur mesurable avec gouvernance rigoureuse",
    descriptionComplete: "Ce pilier transforme le chaos en ordre. Trop d'organisations TI naviguent à vue, réagissant aux urgences sans vision d'ensemble. Un portefeuille projets structuré avec méthode de priorisation claire (valeur/risque/dépendances), roadmap transparente et comité de gouvernance actif change radicalement la donne. Le Directeur TI devient orchestrateur stratégique plutôt que pompier permanent. Chaque projet doit justifier son existence par ROI mesurable, s'intégrer dans un plan cohérent et être suivi rigoureusement. Les bilans post-projets capturent les apprentissages pour améliorer continuellement la performance organisationnelle.",
    objectifStrategique: "Atteindre 90% de projets livrés dans les délais et budgets avec ROI positif démontré",
    beneficesCles: [
      "Vision claire 12-18 mois permettant anticipation et planification",
      "Priorisation objective basée sur valeur métier évitant projets 'politiques'",
      "ROI moyen projets : +165% démontrant création valeur tangible",
      "Gaspillage réduit : arrêt rapide projets non performants (méthode stop/start/continue)",
      "Apprentissage organisationnel via bilans post-projets capitalisés"
    ],
    risquesSiAbsent: [
      "Navigation à vue : équipe TI submergée sans priorisation claire",
      "Gaspillage massif : projets lancés puis abandonnés sans bilan (coût irrécupérable)",
      "Démotivation équipe : efforts sur projets qui n'apportent jamais valeur visible",
      "Perte confiance direction : dépassements budgets/délais chroniques",
      "Opportunités manquées : ressources gaspillées sur projets faible valeur"
    ],
    icon: "FolderKanban",
    couleur: "from-purple-600 to-pink-600",
    asWas: 1.5,
    asIs: 2.8,
    target: 4.4,
    nombreTactiques: 10,
    tactiquesActives: 6
  },
  {
    id: 4,
    nom: "Gestion des Services TI",
    description: "Livrer un service de qualité au quotidien selon normes ITIL",
    descriptionComplete: "L'excellence opérationnelle est le socle de la crédibilité TI. Peu importe vos projets stratégiques brillants, si les employés attendent 3 jours une réinitialisation mot de passe ou si le réseau tombe chaque semaine, votre réputation est ruinée. Ce pilier ITIL 4 structure les processus Incident, Problem, Change et Service Request. Des SLA clairs, mesures rigoureuses (temps résolution, taux premier contact, CSAT), monitoring proactif et base connaissance enrichie transforment un support réactif en service prévisible et professionnel. La disponibilité 99.5%+ des systèmes critiques devient la norme, pas l'exception.",
    objectifStrategique: "Délivrer un support TI de classe mondiale : SLA respectés 95%, CSAT 85%+, disponibilité 99.5%+",
    beneficesCles: [
      "Productivité employés maximisée : incidents résolus rapidement (<4h moyenne)",
      "Satisfaction utilisateurs élevée : CSAT 84%, NPS +45",
      "Continuité affaires assurée : disponibilité systèmes 99.7%",
      "Équipe support performante : taux résolution 1er contact 68% (cible 75%)",
      "Coûts optimisés : automatisation reporting, processus standardisés ITIL"
    ],
    risquesSiAbsent: [
      "Frustration généralisée : employés perdant temps avec problèmes TI récurrents",
      "Pertes financières : heures productives perdues à cause pannes évitables",
      "Démotivation équipe TI : débordée par tickets et firefighting permanent",
      "Réputation TI désastreuse : vue comme incompétente par organisation",
      "Risques opérationnels : absence plans continuité = vulnérabilité incidents majeurs"
    ],
    icon: "Headphones",
    couleur: "from-orange-600 to-amber-600",
    asWas: 2.5,
    asIs: 3.8,
    target: 4.6,
    nombreTactiques: 10,
    tactiquesActives: 9
  },
  {
    id: 5,
    nom: "Cybersécurité",
    description: "Protéger l'organisation de manière proactive selon NIST 2.0",
    descriptionComplete: "La cybersécurité n'est plus optionnelle. Une seule brèche peut coûter des millions et détruire une réputation bâtie sur des décennies. Ce pilier implémente le framework NIST CSF 2.0 (Identify, Protect, Detect, Respond, Recover) avec rigueur. Au-delà des technologies (EDR/SIEM, MFA, patchs), c'est un changement culturel : former TOUS les employés (phishing), tester régulièrement (pentests), mesurer maturité (score NIST), gérer risques tiers, et maintenir plans reprise (PRA/BCP). Une organisation mature atteint 85%+ conformité NIST et peut démontrer résilience face menaces croissantes.",
    objectifStrategique: "Atteindre 85%+ conformité NIST 2.0 avec résilience prouvée face aux cybermenaces",
    beneficesCles: [
      "Protection réputation : éviter brèches coûteuses (coût moyen: 4.45M$ USD/brèche)",
      "Conformité assurée : ISO 27001, NIST, Loi 25, évitant amendes réglementaires",
      "Détection rapide menaces : EDR/SIEM avec temps détection <15min",
      "Employés sensibilisés : taux échec phishing réduit à <10% post-formation",
      "Continuité garantie : plans PRA testés assurant reprise rapide post-incident"
    ],
    risquesSiAbsent: [
      "Catastrophe financière : brèche majeure coûtant millions + arrêt opérations",
      "Perte confiance clients : données clients compromises = exode massif",
      "Sanctions légales : amendes RGPD/Loi 25 + poursuites civiles",
      "Ransomware paralysant : organisation entière bloquée sans accès systèmes critiques",
      "Faillite potentielle : 60% PME victimes cyberattaque ferment dans 6 mois"
    ],
    icon: "Shield",
    couleur: "from-red-600 to-rose-600",
    asWas: 1.9,
    asIs: 3.1,
    target: 4.7,
    nombreTactiques: 6,
    tactiquesActives: 6
  },
  {
    id: 6,
    nom: "Amélioration continue / maturité TI",
    description: "Ne pas subir le changement, le créer activement",
    descriptionComplete: "L'innovation n'est pas réservée aux startups Silicon Valley. Toute organisation TI peut cultiver culture d'amélioration continue (Kaizen TI). Ce pilier réserve 8-10% budget TI à l'exploration : POC nouvelles technologies, automatisation processus manuels, veille stratégique, ateliers créativité. L'objectif est double : réduire coûts opérationnels (automatisation = -35% temps tâches répétitives) ET créer avantages compétitifs (adoption précoce IA, outils modernes). Un indicateur clé : % temps équipe dédié innovation (cible 10-15% vs urgences). L'innovation devient systémique via funnel structuré: idée → évaluation → POC → déploiement.",
    objectifStrategique: "Dédier 10% budget et temps à innovation créant avantages compétitifs mesurables",
    beneficesCles: [
      "Réduction coûts : automatisation 12 processus économisant 85k$/an",
      "Compétitivité renforcée : adoption précoce technologies créant différenciation",
      "Efficacité accrue : coût TI par utilisateur réduit 5%/an (actuel: 9,067$)",
      "Équipe motivée : temps alloué exploration stimulant créativité et rétention talents",
      "Pipeline innovation : POC structurés avec 50% conversion en projets productifs"
    ],
    risquesSiAbsent: [
      "Obsolescence graduelle : technologies vieillissantes perdant compétitivité",
      "Inefficacité croissante : processus manuels coûteux jamais automatisés",
      "Démotivation talents : meilleurs éléments quittant pour environnements plus innovants",
      "Opportunités manquées : concurrents adoptant IA/automatisation pendant que vous stagner",
      "Coûts croissants : absence optimisation continue = coûts TI dérivant à la hausse"
    ],
    icon: "Lightbulb",
    couleur: "from-cyan-600 to-blue-600",
    asWas: 1.3,
    asIs: 2.4,
    target: 4.0,
    nombreTactiques: 10,
    tactiquesActives: 4
  },
  {
    id: 7,
    nom: "Innovation & IA",
    description: "Adopter l'IA de manière stratégique et éthique",
    descriptionComplete: "L'IA est la révolution technologique de notre époque, comparable à Internet années 90. Ce pilier guide adoption IA stratégique et éthique : définir vision IA alignée objectifs entreprise, identifier 8-10 cas usage concrets (chatbot support, analyse prédictive, automatisation documents), tester via POC mesurables, former 90% équipes, et encadrer via charte éthique/légale (biais, transparence, Loi 25). L'IA n'est pas magique mais outil puissant si bien utilisé. Le ROI moyen POC IA atteint +185%. Les organisations maîtrisant IA gagnent avantage compétitif durable. CODEHUB structure backlog développement avec CI/CD DevSecOps pour industrialisation.",
    objectifStrategique: "Déployer 8 cas usage IA productifs avec ROI +150% et adoption 90% des équipes",
    beneficesCles: [
      "Gains productivité massifs : IA réduisant tâches répétitives de 40-60%",
      "Expérience client améliorée : chatbots IA répondant 24/7 avec satisfaction 80%+",
      "Décisions data-driven : analyse prédictive détectant tendances invisibles humains",
      "Innovation accélérée : POC IA avec 67% taux conversion et ROI +185%",
      "Avantage compétitif : adoption IA créant différenciation marché significative"
    ],
    risquesSiAbsent: [
      "Obsolescence rapide : concurrents IA-powered distançant organisation rapidement",
      "Inefficacité perpétuée : tâches manuelles que IA pourrait éliminer continuant gaspiller temps",
      "Perte talents : meilleurs employés quittant pour organisations exploitant IA",
      "Coûts opportunité énormes : revenus/économies potentiels IA non capturés",
      "Disruption subie : risque disruption par nouveaux entrants IA-native"
    ],
    icon: "Sparkles",
    couleur: "from-violet-600 to-purple-600",
    asWas: 0.8,
    asIs: 2.1,
    target: 4.2,
    nombreTactiques: 9,
    tactiquesActives: 5
  }
];

// Les 65 Tactiques (alignées sur NIST CSF 2.0 pour le Pilier 5)
export const tactiques: Tactique[] = [
  // PILIER 1 : Besoins et alignement TI (11 tactiques)
  {
    id: "P1-T01",
    nom: "Évaluer régulièrement les besoins d'affaires avec les directions",
    description: "Maintenir un alignement constant entre TI et objectifs d'affaires",
    descriptionDetaillee: "Cette tactique fondamentale établit un dialogue structuré et récurrent avec chaque direction (RH, Ventes, Finance, Opérations, Marketing). Via rencontres semestrielles d'1h30-2h, le Directeur TI collecte besoins, frustrations, opportunités technologiques alignées aux objectifs départementaux. Un questionnaire standardisé capture : douleurs actuelles, projets en vue, budget disponible, priorités 6-12 mois. Le livrable est une matrice consolidée croisant besoins vs impact/faisabilité permettant roadmap TI alignée stratégie corporative. Cette approche transforme TI réactive ('donnez-moi ce que je demande') en proactive ('voici ce qui maximise votre performance').",
    casUsage: "Exemple TechnoSolutions : Rencontre avec Directrice Ventes révèle besoin CRM mobile performant. Impact: +20% productivité commerciale (+340k$ revenus annuels). Priorisé en tête roadmap vs autres projets moindre valeur. Résultat: satisfaction Ventes passée de 6/10 à 9/10, TI perçues comme enabler stratégique.",
    impactMetier: "Satisfaction directions +25%, Projets TI alignés stratégie +40%, ROI moyen projets +165%",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Tous directeurs départements",
      informed: "Équipe TI"
    },
    livrables: ["Rapports semestriels des besoins TI", "Matrice de priorisation validée", "Plan de mise à jour continue"],
    livrablesVisuels: [
      {
        nom: "Matrice Besoins vs Priorités",
        type: "Matrice",
        description: "Tableau 2x2 Impact/Faisabilité positionnant 15-20 besoins identifiés, facilitant décisions priorisation",
        imageUrl: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Alignement Stratégique",
        type: "Dashboard",
        description: "Vue consolidée : % directions rencontrées, satisfaction moyenne (NPS), nombre besoins identifiés/traités",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Semestriel Besoins",
        type: "Rapport",
        description: "Document 10-15 pages : synthèse rencontres, insights clés, recommandations priorisées, roadmap ajustée",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% directions rencontrées par semestre (objectif: 100%)",
    echeance: "Semestriel"
  },
  {
    id: "P1-T02",
    nom: "Cartographier les projets TI aux priorités stratégiques",
    description: "Visualiser comment les projets TI soutiennent les priorités stratégiques",
    descriptionDetaillee: "Cette cartographie visuelle relie chaque projet TI aux 3-5 priorités stratégiques corporatives (ex: croissance revenus 15%, réduction coûts 10%, expansion géographique, satisfaction client 90%+). Matrice ou diagramme Sankey montre flux investissements TI vers objectifs business. Permet identifier projets orphelins (sans lien stratégie = candidats arrêt) et lacunes (priorités stratégiques sans support TI = opportunités). Mise à jour trimestrielle lors comité TI valide alignement continu. Cet outil de communication puissant démontre direction comment TI sert stratégie, pas agenda technologique déconnecté.",
    casUsage: "TechnoSolutions cartographie 12 projets TI vs 4 priorités stratégiques. Découvre: 2 projets (18M$ budget) ne supportent aucune priorité = arrêt immédiat économisant 18k$. Identifie priorité 'Expansion Ontario' sans support TI = lance projet CRM bilingue (ROI: +120k$/an nouveaux clients). Direction visualise clairement où va argent TI et pourquoi.",
    impactMetier: "Alignement projets +40%, Gaspillage réduit 18k$, Communication direction +60%, Priorisation objective",
    pilier: 1,
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Comité TI",
      informed: "Toute l'entreprise"
    },
    livrables: ["Carte visuelle projets-priorités", "Tableau de suivi alignement", "Documentation gouvernance"],
    livrablesVisuels: [
      {
        nom: "Diagramme Sankey Alignement",
        type: "Présentation",
        description: "Flux visuels montrant budget TI (680k$) vers 4 priorités stratégiques, largeurs proportionnelles investissements",
        imageUrl: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&auto=format&fit=crop"
      },
      {
        nom: "Matrice Projets vs Priorités",
        type: "Matrice",
        description: "Tableau 12 projets x 4 priorités avec scores impact, identification projets orphelins et lacunes",
        imageUrl: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Alignement Stratégique",
        type: "Dashboard",
        description: "Métriques: % projets alignés 75%, budget aligné 82%, lacunes stratégiques 1, tendance trimestrielle",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets alignés avec priorités stratégiques (actuel: 75%, cible: 95%)"
  },
  {
    id: "P1-T03",
    nom: "Suivre le budget TI (% du CA, coût par employé)",
    description: "Mesurer niveau d'investissement TI vs revenus et taille organisation",
    descriptionDetaillee: "KPI financier stratégique comparant investissement TI à la taille organisationnelle. Budget TI % CA indique maturité technologique (secteur services: 7-10% typique, manufacturing: 3-5%). Coût TI/employé révèle efficacité (benchmark PME services: 8-12k$/employé/an). Dashboard trimestriel track évolution vs benchmarks Gartner/IDC. Permet justifier budgets ('nous sommes 8% CA, industrie moyenne 8.5%'), identifier sous/sur-investissement, et démontrer optimisation continue (-5%/an coût/employé via automatisation). CFO apprécie ce langage financier vs jargon technique. Comparaison pluriannuelle montre trajectoire amélioration.",
    casUsage: "TechnoSolutions budget TI 680k$ = 8% CA de 8.5M$ et 9,067$/employé (75 emp). Benchmark révèle: légèrement sous moyenne secteur (8.5% CA, 9,500$/emp). Justification augmentation budget 5% (34k$) pour combler lacune cybersécurité NIST. Dashboard montre optimisation: coût/employé réduit 9,850$ → 9,067$ en 18 mois (-8%) via automatisation 12 processus.",
    impactMetier: "Justification budgets objective, Optimisation -5%/an, Benchmarks compétitifs, Langage CFO/direction",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "CFO",
      consulted: "Direction générale",
      informed: "Comité TI"
    },
    livrables: ["Tableau budgétaire trimestriel", "Rapport benchmark industrie", "Coûts par employé"],
    livrablesVisuels: [
      {
        nom: "Dashboard Budgétaire TI",
        type: "Dashboard",
        description: "Métriques temps réel: Budget 680k$ (8% CA), 9,067$/emp, évolution vs benchmarks, graphiques tendances",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Benchmark Gartner/IDC",
        type: "Rapport",
        description: "Analyse comparative 8 pages: positionnement vs industrie, recommandations investissement, ROI potentiel",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Budget TI: 8% CA | 9,067$/employé (benchmark: 7-10% | 8-12k$)"
  },
  {
    id: "P1-T04",
    nom: "Mesurer satisfaction des directions internes (surveys)",
    description: "Détecter frustrations et démontrer impact positif des initiatives",
    descriptionDetaillee: "Sondage semestriel quantitatif (questionnaire 15-20 questions, échelle 1-10, temps: 8-10 min) auprès TOUTES directions (RH, Ventes, Finance, Ops, Marketing). Mesure: qualité support TI, alignement besoins, délai réponse, communication, valeur perçue services TI. Analyse NPS (Net Promoter Score): % promoteurs (9-10) - % détracteurs (0-6) = score -100 à +100. Section commentaires ouverts capture frustrations spécifiques (ex: 'VPN lent', 'CRM manque fonctions mobiles'). Synthèse top 5 irritants génère plan action priorisé. Suivi semestriel montre évolution satisfaction post-correctifs. Benchmark interne: satisfaction 78% → cible 85%+.",
    casUsage: "TechnoSolutions sondage révèle satisfaction globale 78%, NPS +32 (bon mais améliorable). Top irritants: 1) VPN lent télétravail (43% mentions), 2) Temps résolution tickets longs (38%), 3) CRM mobile limité (31%). Actions: déploiement VPN nouvelle génération (satisfaction VPN passe 6.2 → 8.9/10), portail self-service (-30% tickets), upgrade CRM mobile. Sondage suivant 6 mois: satisfaction 78% → 84%, NPS +32 → +48.",
    impactMetier: "Satisfaction directions +25%, Irritants identifiés/résolus, NPS +48, Communication bidirectionnelle TI-métier",
    pilier: 1,
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 4,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Directions internes",
      informed: "Équipe TI"
    },
    livrables: ["Rapport sondage semestriel", "Synthèse irritants", "Plan amélioration"],
    livrablesVisuels: [
      {
        nom: "Dashboard Satisfaction Temps Réel",
        type: "Dashboard",
        description: "Métriques: Satisfaction 78%, NPS +32, taux participation 87%, graphiques par direction, évolution temporelle",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Sondage Semestriel",
        type: "Rapport",
        description: "Analyse 12 pages: résultats quantitatifs, commentaires anonymisés, top irritants, plan action priorisé",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Score satisfaction: 78% (cible: 85%+)"
  },
  {
    id: "P1-T05",
    nom: "Aligner objectifs TI avec OKR de l'entreprise",
    description: "Garantir que chaque action TI contribue aux résultats corporatifs",
    descriptionDetaillee: "Framework OKR (Objectives & Key Results) popularisé par Google structure objectifs corporatifs en objectifs mesurables + résultats clés quantifiés. Ex OKR entreprise: 'Devenir leader marché Ontario' (Objectif) → KR1: Croissance revenus Ontario +30%, KR2: NPS clients Ontario >80, KR3: Parts marché 15% → 22%. TI crée OKR TI contributeurs: 'Supporter expansion Ontario' → KR1: CRM bilingue déployé 100% force vente, KR2: Infrastructure cloud scalable +50% capacité, KR3: Temps réponse applications <2s. Tableau Excel/Notion relie chaque OKR TI à OKR corporatif parent. Revue trimestrielle valide progrès et ajuste. Transparence totale: tous employés voient comment travail TI sert vision entreprise.",
    casUsage: "TechnoSolutions 4 OKR corporatifs 2024-2025. TI définit 8 OKR TI contributeurs. Ex: OKR Corp 'Croissance 15%' → OKR TI 'Infrastructure supportant 100 users → 150 users' (KR: Serveurs scalés, bande passante +50%, temps réponse <2s). Tableau montrant 90% OKR TI alignés (8/9, 1 OKR TI orphelin = obsolète, arrêté). Transparence augmente motivation équipe TI (+18% engagement sondage interne).",
    impactMetier: "Alignement stratégique 90% → 100%, Transparence organisationnelle, Motivation équipe +18%, Langage commun direction-TI",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Dany CA/CE",
      informed: "Toute l'entreprise"
    },
    livrables: ["Tableau alignement TI-OKR", "Revue annuelle cohérence", "Documentation objectifs"],
    livrablesVisuels: [
      {
        nom: "Tableau OKR Hiérarchique",
        type: "Document",
        description: "Arbre hiérarchique: 4 OKR corporatifs → 8 OKR TI contributeurs, KR mesurables, progression %",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard OKR Temps Réel",
        type: "Dashboard",
        description: "Progression trimestrielle 8 OKR TI, scores 0-100%, graphiques tendances, alertes sous-performance",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% objectifs TI alignés aux OKR: 90% (cible: 100%)"
  },
  {
    id: "P1-T06",
    nom: "Tenir revue trimestrielle TI avec direction",
    description: "Présenter résultats, ajuster priorités, prendre décisions stratégiques",
    descriptionDetaillee: "Comité TI trimestriel formel (2-3h, participants: DG, CFO, directeurs départements, Directeur TI BZ) structure gouvernance TI. Agenda standardisé: 1) Revue trimestre écoulé (projets livrés, KPI, incidents majeurs, budget), 2) État portefeuille projets actuel (RAG status: Red/Amber/Green), 3) Nouvelles demandes TI évaluation/priorisation, 4) Décisions go/no-go projets, 5) Ajustements roadmap/budget, 6) AOB. Présentation PowerPoint 25-30 slides professionnelle avec visuels. Procès-verbal détaillé distribué 48h post-rencontre capture décisions, actions, responsables, échéances. Cérémonie trimestrielle ancre TI dans gouvernance corporative, démontre sérieux/professionnalisme, et aligne continuellement direction.",
    casUsage: "TechnoSolutions 4 comités TI 2024 (100% respect calendrier = excellence gouvernance). T3 2024 comité décisions clés: 1) Go projet upgrade CRM mobile (budget 45k$, ROI +120k$/an), 2) No-go projet portail employés (valeur faible vs coût), 3) Ajustement roadmap: prioriser cybersécurité NIST post-audit révélant lacunes. Procès-verbal 8 pages distribué 36h. Suivi T4: 95% actions T3 complétées. Direction apprécie rigueur gouvernance.",
    impactMetier: "Gouvernance rigoureuse 4/4 comités, Décisions structurées, Alignement continu, Crédibilité direction accrue",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 5,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Comité TI",
      informed: "Directions"
    },
    livrables: ["Présentations trimestrielles", "Procès-verbaux comité", "Plan suivi décisions"],
    livrablesVisuels: [
      {
        nom: "Présentation Comité TI Q3",
        type: "Présentation",
        description: "PowerPoint 28 slides: résultats trimestre, portefeuille projets RAG, nouvelles demandes, décisions",
        imageUrl: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&auto=format&fit=crop"
      },
      {
        nom: "Procès-Verbal Comité",
        type: "Document",
        description: "Document formel 8 pages: décisions, actions, RACI, échéances, suivi trimestre précédent",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Revues tenues: 4/4 (100% respect calendrier)"
  },
  {
    id: "P1-T07",
    nom: "Communiquer priorités TI aux équipes",
    description: "Créer alignement interne et mobiliser les ressources efficacement",
    descriptionDetaillee: "Communication interne structure (newsletters mensuelles, town halls trimestriels, PRIsME plateforme) diffuse priorités TI à TOUTE organisation. Contenu: roadmap trimestre, projets en cours, succès célébrés, changements à venir, KPI atteints. But: chaque employé comprend où TI investit efforts et pourquoi (alignement stratégie). Sondage test: 'Nommez 3 priorités TI actuelles?' - benchmark 65% réponses correctes → cible 85%+. Communication accrue augmente engagement employés (comprennent pourquoi changements), réduit résistance adoption nouvelles technologies, et démontre transparence TI. Format varié: texte court (300 mots), infographies visuelles, vidéos 2-3 min CEO/Directeur TI.",
    casUsage: "TechnoSolutions lance newsletter mensuelle 'TI Connect' (envoi 1er mardi mois, ouverture 78%, lecture 45%). Contenu sept 2024: Roadmap T4, upgrade VPN nouvelle génération (bénéfices: vitesse x3, sécurité renforcée), KPI support (MTTR 4.2h → 3.8h). Town hall trimestriel 45 min (participation 82%) présente avancement année, célèbre succès équipe. Sondage post-communication: employés nommant priorités passe 65% → 81%. Résistance changements -35%.",
    impactMetier: "Compréhension priorités 65% → 85%, Engagement employés +22%, Résistance changements -35%, Transparence accrue",
    pilier: 1,
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Équipe TI",
      informed: "Tous employés"
    },
    livrables: ["Notes internes mensuelles", "Présentations équipe", "Publication PRIsME"],
    livrablesVisuels: [
      {
        nom: "Newsletter TI Connect",
        type: "Document",
        description: "Newsletter mensuelle 2 pages: roadmap, projets, succès, KPI, design professionnel infographies",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      },
      {
        nom: "Présentation Town Hall Q3",
        type: "Présentation",
        description: "PowerPoint 15 slides: bilan trimestre, succès célébrés, roadmap T4, Q&A interactif",
        imageUrl: "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% employés pouvant nommer priorités: 65% (cible: 85%)"
  },
  {
    id: "P1-T08",
    nom: "Suivre valeur perçue des TI par dirigeants",
    description: "Valider que TI est vue comme créateur de valeur vs centre de coûts",
    descriptionDetaillee: "Perception TI par direction détermine budget, influence, autonomie. Question critique: 'TI est centre coûts ou créateur valeur?' Sondage annuel qualitatif dirigeants (6-8 dirigeants, entrevues 30 min) mesure perception via question: 'Sur échelle 1-10, TI crée combien valeur vs coûte?' Moyenne actuelle: 7.5/10 (bon mais perfectible), cible: 9/10 (excellent). Questions approfondies: projets TI impactant revenus? Exemples valeur créée? Frustrations TI? Rapport synthèse identifie leviers amélioration perception. Actions correctrices: communication ROI projets, démonstrations valeur régulières, quick wins visibles. Suivi annuel montre évolution perception, guide stratégie communication TI.",
    casUsage: "TechnoSolutions entrevues 7 dirigeants révèlent score perception 7.5/10. Feedbacks: 'TI réactive mais manque démonstration ROI tangible', 'Ne vois pas comment TI aide croissance revenus'. Actions: présentation trimestrielle ROI projets TI (CRM mobile: +120k$/an nouveaux clients, automatisation rapports: +65k$/an productivité), célébration succès visibles. Entrevues année suivante: score 7.5 → 8.9/10, commentaires: 'TI maintenant perçues moteur croissance'.",
    impactMetier: "Perception valeur 7.5 → 9/10, Budget TI justifié facilement, Influence TI direction accrue, Projets approuvés +40%",
    pilier: 1,
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Tous dirigeants",
      informed: "Équipe TI"
    },
    livrables: ["Rapport annuel perception", "Score comparatif", "Synthèse feedbacks"],
    livrablesVisuels: [
      {
        nom: "Rapport Perception Valeur TI",
        type: "Rapport",
        description: "Analyse 10 pages: score 7.5/10, feedbacks dirigeants anonymisés, leviers amélioration, plan action",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard ROI Projets TI",
        type: "Dashboard",
        description: "Métriques visuelles: ROI cumulé projets 3 ans, valeur créée 385k$, économies réalisées, graphiques",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Score valeur perçue: 7.5/10 (cible: 9/10)"
  },
  {
    id: "P1-T09",
    nom: "Revoir annuellement partenariats stratégiques",
    description: "Évaluer pertinence, performance et rentabilité des partenaires",
    descriptionDetaillee: "Partenaires TI stratégiques (fournisseurs cloud, éditeurs logiciels, intégrateurs, consultants) représentent 40-60% budget TI externe. Revue annuelle formelle évalue: 1) Performance (SLA respectés, qualité service, réactivité), 2) Rentabilité (prix compétitifs vs marché, optimisations possibles), 3) Innovation (roadmap produit, nouvelles fonctionnalités), 4) Risques (santé financière fournisseur, dépendance excessive, alternatives marché). Matrice valeur/risque 2x2 positionne partenaires. Actions: renégocier contrats sous-performants, diversifier si dépendance critique, explorer alternatives si prix dérive. Processus: rencontres QBR (Quarterly Business Review) fournisseurs, grille évaluation standardisée, rapport synthèse CFO/direction.",
    casUsage: "TechnoSolutions 12 partenaires stratégiques. Revue annuelle révèle: Fournisseur cloud AWS (performance 9/10, coût élevé vs marché +15%) = renégociation contrat économise 12k$/an. Fournisseur ERP score faible 5/10 (support médiocre, prix élevé, roadmap stagnante) = exploration alternative SaaS moderne économise 28k$/an + fonctionnalités supérieures. Diversification réduisant dépendance critique unique fournisseur backup cloud.",
    impactMetier: "Économies annuelles 40k$, Performance fournisseurs +25%, Risques réduits diversification, Optimisation continue",
    pilier: 1,
    priorite: "Moyenne",
    statut: "À faire",
    asWas: 2,
    asIs: 2,
    target: 4,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "CFO",
      consulted: "Fournisseurs",
      informed: "Direction"
    },
    livrables: ["Évaluation partenaires", "Matrice valeur/risque", "Recommandations"],
    livrablesVisuels: [
      {
        nom: "Matrice Valeur/Risque Partenaires",
        type: "Matrice",
        description: "Grille 2x2 positionnant 12 partenaires, actions par quadrant (renégocier, explorer, maintenir)",
        imageUrl: "https://images.unsplash.com/photo-1529078155058-5d716f45d604?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Évaluation Annuelle",
        type: "Rapport",
        description: "Document 15 pages: scores partenaires, benchmarks marché, recommandations, économies potentielles 40k$",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% partenaires évalués annuellement (cible: 100%)"
  },
  {
    id: "P1-T10",
    nom: "Formaliser besoins TI dans spécifications d'exigences",
    description: "Document référence pour encadrer projets et clarifier objectifs",
    descriptionDetaillee: "Cahier charge (CDC) structure formelle demande projet TI AVANT lancement. Empêche syndrome 'on verra en cours route' coûtant dépassements 30-50%. Template standardisé 8-12 pages couvre: 1) Contexte business (pourquoi projet?), 2) Objectifs mesurables (quoi atteindre?), 3) Périmètre fonctionnel détaillé (fonctions requises), 4) Contraintes (budget, délais, technologies), 5) Livrables attendus, 6) Critères succès quantifiés, 7) RACI projet, 8) Risques identifiés. Demandeur complète CDC avec accompagnement Directeur TI. Validation comité TI formelle avant go projet. Archivage PRIsME centralise CDC pour référence future. CDC réduit malentendus -70%, clarifie attentes, et protège équipe TI scope creep.",
    casUsage: "TechnoSolutions projet upgrade CRM démarre avec CDC complet 10 pages. Objectif mesuré: 'Réduire temps saisie opportunités 8 min → 3 min, augmenter taux conversion 18% → 25%'. Périmètre: interface mobile native iOS/Android, synchronisation offline, intégration ERP bidirectionnelle. Budget: 45k$, délai: 4 mois. Critères succès quantifiés évitent discussions floues post-livraison. Projet livré: budget 47k$ (+4% vs 45k$), délai 4.2 mois (+5% vs 4 mois) = excellence vs industrie (moyennes: +30% budget, +45% délai).",
    impactMetier: "Malentendus réduits -70%, Dépassements budgets -80%, Clarté attentes, Scope creep évité, Projets 85% avec CDC",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 1,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Demandeur projet",
      consulted: "Équipe TI",
      informed: "Comité TI"
    },
    livrables: ["Cahier charge structuré", "Modèle standardisé", "Archivage PRIsME"],
    livrablesVisuels: [
      {
        nom: "Template Spécifications d'exigences BZ",
        type: "Document",
        description: "Modèle Word 12 pages: sections structurées, exemples remplis, guidance rédaction, checklist validation",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      },
      {
        nom: "Cahier Charge Projet CRM",
        type: "Document",
        description: "Exemple concret rempli: contexte, objectifs SMART, périmètre détaillé, RACI, critères succès quantifiés",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets avec cahier charge: 85% (cible: 100%)"
  },
  {
    id: "P1-T11",
    nom: "PRIsME - Livre de minutes TI (registre décisions)",
    description: "Assurer traçabilité décisions et mémoire organisationnelle",
    descriptionDetaillee: "PRIsME = plateforme centralisée (Notion, SharePoint, Confluence) archivant TOUTES décisions TI stratégiques. Livre minutes structure: procès-verbaux comités TI, décisions architecturales, choix fournisseurs, arbitrages budgétaires, changements majeurs. Format standardisé: date, participants, décisions prises, rationale, RACI, échéances, statut suivi. Index searchable permet retrouver historique décision ('Pourquoi choisi fournisseur X vs Y en 2022?'). Distribué 48h post-réunion, accessible direction/parties prenantes autorisées. Valeur: mémoire organisationnelle préservée (évite refaire analyses identiques), traçabilité décisions (audit, conformité), onboarding accéléré nouveaux directeurs TI, et continuité si changement personnel.",
    casUsage: "TechnoSolutions PRIsME contient 127 décisions TI archivées 2022-2024. Ex: PV comité T2 2023 documente décision migration cloud AWS vs Azure (5 pages analyse comparative, décision AWS basée coûts -12%, écosystème Python équipe, support francophone). 18 mois plus tard, nouveau VP Finance questionne choix: 2 min retrouve PV, comprend rationale, valide pertinence décision. Sans PRIsME: rediscussions stériles, temps perdu, risque mauvaises décisions par oubli contexte historique.",
    impactMetier: "Traçabilité 100% décisions, Mémoire organisationnelle préservée, Onboarding nouveaux -60% temps, Audit conformité facilité",
    pilier: 1,
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 5,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Comité TI",
      informed: "Parties prenantes"
    },
    livrables: ["Livre minutes PRIsME", "Index décisions", "Accès partagé direction"],
    livrablesVisuels: [
      {
        nom: "Plateforme PRIsME",
        type: "Outil",
        description: "Interface centralisée: 127 décisions archivées, search full-text, filtres date/catégorie, accès contrôlé",
        imageUrl: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop"
      },
      {
        nom: "Procès-Verbal Type Comité TI",
        type: "Document",
        description: "Template PV 6 pages: participants, décisions, rationale, RACI, actions, échéances, format standardisé",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% réunions avec minutes: 100% | Délai diffusion: 48h"
  },

  // PILIER 2 : Catalogue actifs et utilisateurs (10 tactiques)
  {
    id: "P2-T01",
    nom: "Maintenir inventaire complet actifs TI",
    description: "Base de gouvernance technologique - savoir ce qu'on possède",
    descriptionDetaillee: "Un inventaire TI exhaustif capture TOUS les actifs : 127 postes, 18 serveurs physiques/virtuels, 43 licences logicielles, équipements réseau (switches, firewalls, AP WiFi), imprimantes, téléphones IP. Chaque actif inclut : numéro série, date acquisition, coût, garantie expiration, utilisateur assigné, localisation physique, configuration. Une CMDB (ServiceNow, Jira Assets, ou Excel structuré pour PME) centralise ces données. Audits trimestriels avec lecteurs code-barres valident cohérence inventaire vs réalité terrain. Cette base permet décisions éclairées : planification remplacements, optimisation licences (42k$ économies annuelles TechnoSolutions), conformité légale (prouver possession licences lors audits éditeurs).",
    casUsage: "TechnoSolutions découvre via inventaire : 22 licences Microsoft 365 E3 inutilisées (8$/user/mois x 22 x 12 = 2,112$/an gaspillés). Action: réallouer licences ou downgrade. Découvre aussi 8 postes >5 ans = risque panne élevé. Budget remplacement planifié Q2 2025 (12k$) évitant pannes coûteuses.",
    impactMetier: "Économies annuelles 42k$, Conformité légale 100%, Temps résolution incidents -40%",
    pilier: 2,
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Technicien TI BZ",
      accountable: "Directeur TI BZ",
      consulted: "Fournisseurs",
      informed: "Finance"
    },
    livrables: ["Base données actifs TI", "Rapports audit réguliers", "CMDB simplifiée"],
    livrablesVisuels: [
      {
        nom: "Dashboard CMDB Actifs",
        type: "Dashboard",
        description: "Vue temps réel : 127 actifs répertoriés, âge moyen 3.2 ans, 12 actifs fin garantie <90j, alertes obsolescence",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Audit Trimestriel",
        type: "Rapport",
        description: "Document 8 pages : écarts détectés, actions correctives, évolution vs trimestre précédent, conformité 92%",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      },
      {
        nom: "Plan Cycle Vie Équipements",
        type: "Processus",
        description: "Timeline visuelle : remplacements planifiés 18 mois, budget prévisionnel, optimisation coûts",
        imageUrl: "https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% actifs enregistrés: 92% (cible: 98%)"
  },
  {
    id: "P2-T02",
    nom: "Maintenir inventaire utilisateurs complet (360°)",
    description: "Profil détaillé: accès, formations, matériel, incidents, rôles",
    descriptionDetaillee: "Vision 360° chaque utilisateur TI centralise: 1) Identité (nom, département, gestionnaire, date embauche), 2) Matériel assigné (laptop, écrans, téléphone, périphériques), 3) Accès logiciels/systèmes (applications autorisées, niveaux accès, date activation), 4) Formations complétées (cybersécurité, outils métiers, dates, certifications), 5) Historique incidents/tickets (fréquence, types problèmes récurrents), 6) Profil utilisation (heures connexion, consommation bande passante si pertinent). Intégration RH automatise création/désactivation comptes. Analytics identifient: utilisateurs super-users (formations avancées = champions internes), comptes à risque (formations cybersécurité manquantes), matériel obsolète nécessitant upgrade.",
    casUsage: "TechnoSolutions profils 360° révèlent insights: 12 employés (16%) sans formation cybersécurité obligatoire = risque phishing élevé, campagne formation ciblée. Analyse incidents révèle 8 utilisateurs (11%) générant 35% tickets support = besoin coaching utilisation outils. Détection 5 comptes orphelins (ex-employés conservant accès) = faille sécurité majeure corrigée. Profils 360° passent 78% complétude → 92% via automatisation intégration RH.",
    impactMetier: "Complétude profils 78% → 95%, Failles sécurité détectées/corrigées, Formation ciblée efficace, Coaching utilisateurs optimisé",
    pilier: 2,
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "RH",
      consulted: "Gestionnaires",
      informed: "Employés"
    },
    livrables: ["Répertoire utilisateurs 360°", "Profils accès/formations", "Historique incidents"],
    livrablesVisuels: [
      {
        nom: "Dashboard Utilisateurs 360°",
        type: "Dashboard",
        description: "Vue consolidée: 75 utilisateurs, complétude 92%, alertes formations manquantes, matériel obsolète",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      },
      {
        nom: "Fiche Utilisateur Type",
        type: "Document",
        description: "Profil complet: identité, matériel assigné, 8 accès systèmes, 3 formations, historique 12 tickets",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% utilisateurs avec fiche complète: 78% (cible: 95%)"
  },
  {
    id: "P2-T03",
    nom: "Vérifier conformité sécurité postes (patchs, antivirus)",
    description: "Réduire risques cyberattaque - postes à jour et protégés",
    descriptionDetaillee: "Postes non patchés = porte ouverte hackers. 93% brèches exploitent vulnérabilités connues patchées. Monitoring automatisé (WSUS, Intune, SCCM) scan hebdomadaire 127 postes: Windows Updates installés, antivirus actif/à jour, version OS supportée. Dashboard conformité: rouge (<80%), ambre (80-95%), vert (95%+). Alertes automatiques utilisateurs postes non-conformes: 'Votre poste nécessite updates critique - 3 jours max'. Escalade gestionnaires si non-respect. Patch Tuesday Microsoft = déploiement patches critiques <7 jours (standard industrie). Antivirus centralisé (Defender ATP, CrowdStrike, SentinelOne) bloque menaces temps réel, reporting centralisé.",
    casUsage: "TechnoSolutions conformité 89% (acceptable mais amélioration nécessaire). Analyse révèle: 8 postes non-conformes chroniques (même utilisateurs négligent updates) = interventions manuelles ciblées + session formation. 3 postes Windows 8.1 (OS obsolète non supporté) = budget remplacement urgent 4,500$. Déploiement patches critiques optimisé: 92% postes patchés <7j vs 67% avant automatisation. Conformité passe 89% → 96%. Zéro incident sécurité postes non-patchés.",
    impactMetier: "Conformité 89% → 98%, Risques cyberattaques -75%, Zéro incident patches, Automatisation monitoring, Temps technicien -40%",
    pilier: 2,
    priorite: "Haute",
    statut: "Fait",
    asWas: 3,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Technicien TI BZ",
      accountable: "Directeur TI BZ",
      consulted: "CyberOps",
      informed: "Tous employés"
    },
    livrables: ["Rapports conformité mensuels", "Tableaux suivi patchs", "Alertes antivirus"],
    livrablesVisuels: [
      {
        nom: "Dashboard Conformité Postes",
        type: "Dashboard",
        description: "Temps réel: 127 postes, conformité 96%, 5 postes alerte, patches en attente, graphiques tendances",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Mensuel Sécurité Postes",
        type: "Rapport",
        description: "Analyse 8 pages: conformité %, postes non-conformes, patches déployés, incidents sécurité, actions",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% postes conformes: 89% (cible: 98%)"
  },
  {
    id: "P2-T04",
    nom: "Processus rapide activation/désactivation comptes",
    description: "Réduire risques sécurité (ex-employés conservant accès)",
    descriptionDetaillee: "Faille sécurité critique: ex-employés conservant accès systèmes jours/semaines post-départ. Statistique: 89% organisations ont comptes orphelins actifs. Processus automatisé intégré RH: 1) ACTIVATION nouveau: RH saisit nouvel employé HRIS → trigger automatique TI → création compte AD, email, accès standards basés rôle → SLA 2h. 2) DÉSACTIVATION départ: RH marque départ HRIS → trigger immédiat TI → désactivation compte AD, révocation accès, backup emails → SLA 30 min MAX. Workflow ServiceNow/Jira automatise, track SLA, alerte dépassements. Audit mensuel vérifie zéro comptes orphelins. Processus réduit risque ex-employé mécontent accédant données sensibles/sabotage.",
    casUsage: "TechnoSolutions pré-automatisation: délais activation 2-5 jours (frustration nouveaux), désactivation 1-3 jours (risque sécurité). Post-automatisation: SLA activation 2h respecté 98%, désactivation 30min respecté 100%. Audit révèle zéro comptes orphelins (vs 5 pré-automatisation). Cas réel: employé démissionne vendredi 16h, compte désactivé 16h25, tentative connexion weekend détectée/bloquée = attaque potentielle évitée. ROI: prévention 1 incident data breach potentiel = économie 50-500k$.",
    impactMetier: "SLA activation 2h (98%), SLA désactivation 30min (100%), Zéro comptes orphelins, Risques data breach éliminés",
    pilier: 2,
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 5,
    target: 5,
    raci: {
      responsible: "Technicien TI BZ",
      accountable: "RH",
      consulted: "Gestionnaires",
      informed: "Employés"
    },
    livrables: ["Workflow automatisé", "SLA définis", "Rapports conformité"],
    livrablesVisuels: [
      {
        nom: "Workflow Gestion Comptes",
        type: "Processus",
        description: "Diagramme flux: HRIS trigger → création/désactivation auto → validation → SLA tracking, alertes",
        imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard SLA Comptes",
        type: "Dashboard",
        description: "Métriques: activations 12 mois (58), désactivations (11), SLA respect 99%, délais moyens, tendances",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Délai activation: 2h | Délai désactivation: 30min (SLA respectés 100%)"
  },
  {
    id: "P2-T05",
    nom: "Automatiser gestion accès et droits (IAM/SSO)",
    description: "Simplifier expérience utilisateur et renforcer sécurité",
    descriptionDetaillee: "IAM (Identity & Access Management) + SSO (Single Sign-On) transforment expérience utilisateurs et sécurité. Problème: employés 15+ mots passe applications (Email, CRM, ERP, RH, compta, outils) = Post-it mots passe faibles, réinitialisations fréquentes (35% tickets support). Solution: SSO (Okta, Azure AD, Google Workspace SSO) = 1 login unique accède toutes apps intégrées. IAM automatise provisioning: nouvel employé rôle 'Ventes' → accès CRM, email, Slack automatiques basés template. Départ = révocation tous accès simultanée. MFA (authentification multi-facteurs) ajoute couche sécurité. Intégration progressive: apps cloud (facile), apps on-premise (complexe). Objectif: 85% accès automatisés, 90% apps intégrées SSO.",
    casUsage: "TechnoSolutions déploie Azure AD SSO phase 1: intégration 8 apps cloud (M365, Salesforce, Slack, Dropbox, Zoom, Jira, DocuSign, Adobe). Résultat utilisateurs: login unique accède 8 apps, zéro Post-it mots passe. Tickets support réinitialisation mots passe -60% (18 tickets/mois → 7). Temps onboarding nouveaux -40% (accès automatiques vs création manuelle 8 comptes). Phase 2 planifie: intégration ERP on-premise (complexe, budget 15k$), apps métiers spécialisées. Objectif 18 mois: 85% accès automatisés (vs 45% actuel).",
    impactMetier: "Tickets mots passe -60%, Onboarding -40%, Sécurité MFA renforcée, Expérience utilisateurs +85%, Accès 45% → 85%",
    pilier: 2,
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 4,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "CFO",
      consulted: "Fournisseur IAM",
      informed: "Tous employés"
    },
    livrables: ["Plateforme IAM/SSO", "Tableau rôles automatisés", "Documentation intégration"],
    livrablesVisuels: [
      {
        nom: "Architecture IAM/SSO",
        type: "Processus",
        description: "Diagramme: Azure AD central → 8 apps intégrées SSO, flux authentification MFA, provisioning auto",
        imageUrl: "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard IAM Adoption",
        type: "Dashboard",
        description: "Métriques: 8 apps intégrées, 60% adoption SSO, tickets -60%, roadmap intégrations phases 2-3",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% accès automatisés: 45% (cible: 85%) | % apps intégrées: 60%"
  },
  {
    id: "P2-T06",
    nom: "Audits trimestriels actifs et accès",
    description: "Vérifier cohérence inventaire vs réalité, identifier écarts",
    descriptionDetaillee: "Audits trimestriels formels valident cohérence données vs réalité terrain. Processus: 1) Audit actifs physiques - échantillonnage 25% postes (inspection visuelle,lecteur code-barres, vérification numéros série vs inventaire). 2) Audit accès logiques - revue comptes utilisateurs actifs vs employés RH, détection comptes orphelins, validation droits accès cohérents avec rôles. 3) Identification écarts - actifs non enregistrés (shadow IT), actifs enregistrés manquants (vol/perte), accès excessifs (violation least privilege), comptes inactifs. Rapport audit 10-15 pages catégorise écarts criticité (rouge/ambre/vert), plan action priorisé, suivi correction. KPI: 95%+ écarts corrigés <5 jours.",
    casUsage: "TechnoSolutions audit T3 2024 révèle: 18 écarts détectés (12 actifs, 6 accès). Actifs: 3 laptops non enregistrés (shadow IT département marketing), 2 postes enregistrés inexistants (obsolètes non retirés inventaire). Accès: 2 comptes orphelins (ex-employés 2023), 4 utilisateurs droits admin excessifs (violation least privilege). Actions: enregistrement 3 laptops, nettoyage inventaire, désactivation comptes orphelins, révocation droits admin. 95% écarts corrigés 4 jours. Audit T4: 5 écarts seulement (amélioration -72%).",
    impactMetier: "Écarts détectés/corrigés 95%, Délai correction 5j, Conformité inventaire +15%, Shadow IT éliminé, Accès sécurisés",
    pilier: 2,
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Auditeur externe",
      informed: "Finance"
    },
    livrables: ["Rapports audit trimestriels", "Liste écarts corrigés", "Plan action"],
    livrablesVisuels: [
      {
        nom: "Rapport Audit Trimestriel",
        type: "Rapport",
        description: "Document 12 pages: 18 écarts détectés, catégorisation rouge/ambre/vert, plan action, suivi corrections",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Audit Conformité",
        type: "Dashboard",
        description: "Métriques: écarts par trimestre, taux correction 95%, délai moyen 4j, évolution amélioration -72%",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% écarts détectés corrigés: 95% | Délai moyen correction: 5 jours"
  },
  {
    id: "P2-T07",
    nom: "Documenter configurations standards (CMDB)",
    description: "Faciliter maintenance et assurer homogénéité organisation",
    descriptionDetaillee: "Configurations standards documentées accélèrent déploiement, réduisent erreurs, facilitent support. CMDB (Configuration Management Database) capture: 1) Postes travail - OS (Windows 11 Pro), applications standards (M365, Teams, antivirus, VPN), config réseau (DHCP/DNS), templates images Golden Master. 2) Serveurs - specs hardware, OS/versions, applications hébergées, configurations réseau, sauvegardes. 3) Équipements réseau - switches/routeurs configs, VLANs, ACLs, firmware versions. Documentation permet: déploiement rapide postes identiques (image Golden Master), troubleshooting accéléré (config connue), compliance audits (prouver configurations standard), disaster recovery (rebuild serveurs rapidement). Target: 90% actifs documentés.",
    casUsage: "TechnoSolutions documente configs: Golden Master poste Windows 11 Pro + 12 apps standards = déploiement nouveau poste 45min vs 4h manuel pré-documentation (-82% temps). Serveur AD documenté: rebuild après panne 3h vs 12h sans doc (-75%). Switches réseau configs sauvegardées: restauration post-défaillance 20min. Documentation 70% → 88% actifs (target 90% proche). ROI: temps technicien économisé 120h/an = 7,200$ (60$/h x 120h).",
    impactMetier: "Documentation 70% → 90%, Déploiements -82% temps, Troubleshooting -60%, DR accéléré, ROI 7,200$/an",
    pilier: 2,
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: {
      responsible: "Technicien TI BZ",
      accountable: "Directeur TI BZ",
      consulted: "Équipe TI",
      informed: "Support"
    },
    livrables: ["Base documentaire configs", "Modèle CMDB simplifiée", "Templates déploiement"],
    livrablesVisuels: [
      {
        nom: "CMDB Configurations Standards",
        type: "Document",
        description: "Base centralisée: Golden Master poste, serveurs specs, configs réseau, 88% actifs documentés",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      },
      {
        nom: "Templates Déploiement",
        type: "Processus",
        description: "Guide step-by-step: déploiement poste 45min, serveur rebuild 3h, configs réseau standardisées",
        imageUrl: "https://images.unsplash.com/photo-1523475496153-3d6cc0f0bf19?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% actifs documentés: 70% (cible: 90%)"
  },
  {
    id: "P2-T08",
    nom: "Gestion cycle de vie équipements (achat → retrait)",
    description: "Planifier dès l'achat pour réduire pannes et optimiser coûts",
    descriptionDetaillee: "Cycle vie équipements structuré évite pannes coûteuses, optimise budgets. Phases: 1) ACHAT - specs standards basées besoins (laptop business 1,200$, dev workstation 2,500$), garanties 3 ans on-site. 2) DÉPLOIEMENT - enregistrement inventaire, config standard, assignation utilisateur. 3) MAINTENANCE - suivi âge, renouvellement garanties si pertinent, upgrades mémoire/SSD prolongeant vie. 4) REMPLACEMENT - règle: laptops 4 ans, desktops 5 ans, serveurs 5 ans, switches 7 ans. Planning remplacements génère budget prévisionnel 3 ans. 5) RETRAIT - effacement données certifié (NIST 800-88), recyclage écologique, désenregistrement inventaire. Équipements hors garantie = risque panne +300%, temps résolution +150%.",
    casUsage: "TechnoSolutions gère 127 postes âge moyen 3.2 ans (excellent, cible <4 ans). Plan cycle vie identifie: 12 postes >4 ans nécessitant remplacement 2025 (budget 18k$), 8 postes 3-4 ans surveillance. Cas: laptop 5 ans panne carte-mère hors garantie = réparation impossible économiquement, remplacement urgent non budgété (coût 1,500$ vs 250$ si planifié). Avec planning: zéro dépense urgence non budgétée, taux panne -60%, satisfaction utilisateurs +25% (postes performants récents). ROI: évitement 6 pannes urgentes/an = 9k$ économisé.",
    impactMetier: "Âge moyen 3.2 ans (<4 cible), Pannes -60%, Remplacements planifiés 85%, Urgences non-budgétées zéro, ROI 9k$/an",
    pilier: 2,
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "CFO",
      consulted: "Finance",
      informed: "Utilisateurs"
    },
    livrables: ["Plan cycle vie par catégorie", "Tableau remplacements planifiés", "Budget prévisionnel"],
    livrablesVisuels: [
      {
        nom: "Plan Cycle Vie Équipements",
        type: "Document",
        description: "Règles par catégorie: laptops 4 ans, desktops 5 ans, timeline remplacements 3 ans, budget 54k$",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Cycle Vie",
        type: "Dashboard",
        description: "Métriques temps réel: 127 postes, âge moyen 3.2 ans, 12 remplacements 2025, alertes >4 ans",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Âge moyen équipements: 3.2 ans (cible: <4 ans) | % remplacés selon plan: 85%"
  },
  {
    id: "P2-T09",
    nom: "Gérer licences logicielles et optimiser usage",
    description: "Réduire coûts, assurer conformité légale, optimiser usage",
    descriptionDetaillee: "Gaspillage licences logicielles = 20-30% budgets IT PME. Gestion proactive: 1) Inventaire exhaustif - 43 licences (M365, Adobe CC, Salesforce, AutoCAD, etc), quantités, coûts annuels, dates renouvellement. 2) Monitoring usage réel - analytics utilisation (dernière connexion, fréquence, fonctions utilisées). 3) Optimisation - réaffectation licences inutilisées, downgrade plans surdimensionnés (E5 → E3 si fonctionnalités avancées non-utilisées), élimination doublons (3 outils similaires → 1 standard). 4) Négociations - renégociations annuelles basées volumes réels, comparaisons prix marché, licences multi-années (discount 10-15%). 5) Compliance - audits éditeurs passés sans pénalité (preuves achats/usage).",
    casUsage: "TechnoSolutions audit licences révèle opportunités: 22 licences M365 E3 inutilisées (dernière connexion >90j) = réaffectation vs achat nouveaux (économie 2,112$/an). 5 licences Adobe CC utilisateurs occasionnels (2-3x/mois) = passage abonnement teams shared (économie 1,800$/an). ERP 15 licences nominatives vs 10 utilisateurs simultanés réels = passage licences concurrentes (économie 12k$/an). Élimination 3 outils collaboration redondants gardant Teams (économie 8,500$/an). Total économies: 42k$/an (6.2% budget TI 680k$). Taux utilisation licences 73% → 91%.",
    impactMetier: "Économies 42k$/an (6.2% budget), Utilisation 73% → 91%, Gaspillage éliminé, Compliance 100%, Négociations optimisées",
    pilier: 2,
    priorite: "Haute",
    statut: "Fait",
    asWas: 3,
    asIs: 4,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "CFO",
      consulted: "Fournisseurs",
      informed: "Utilisateurs"
    },
    livrables: ["Tableau gestion licences", "Rapport optimisation annuel", "Alertes renouvellement"],
    livrablesVisuels: [
      {
        nom: "Dashboard Gestion Licences",
        type: "Dashboard",
        description: "Métriques temps réel: 43 licences, utilisation 91%, renouvellements à venir, économies 42k$/an",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Optimisation Annuel",
        type: "Rapport",
        description: "Analyse 15 pages: audit usage, opportunités identifiées, négociations, économies réalisées 42k$",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% licences utilisées vs achetées: 87% (cible: 90%+) | Économies: 42k$/an"
  },
  {
    id: "P2-T10",
    nom: "Suivre obligations légales (RGPD, Loi 25)",
    description: "Éviter sanctions financières et atteintes réputation",
    descriptionDetaillee: "Non-conformité légale = amendes massives + dommages réputation. Loi 25 (Québec): protection renseignements personnels, amendes jusqu'à 25M$ ou 4% CA global (similaire RGPD Europe). Obligations: 1) Inventaire données personnelles (qui, quoi, où, pourquoi), 2) Consentements explicites collecte/usage, 3) Droit accès/rectification/effacement (demandes traitées <30j), 4) Sécurisation données (chiffrement, contrôles accès), 5) Notification brèches <72h, 6) Évaluations impact vie privée (EIVP) projets collectant données sensibles, 7) Agent protection données désigné. Registre conformité Excel track 45 obligations, statut (conforme/partiel/non-conforme), actions, échéances. Revue trimestrielle comité TI.",
    casUsage: "TechnoSolutions audit Loi 25 révèle conformité 78% (lacunes critiques). Conformité: inventaire données, sécurisation, agent désigné. NON-conforme: consentements explicites manquants formulaires web (risque: amendes), EIVP jamais faites projets CRM/RH, processus demandes accès/effacement inexistant (délai légal 30j non respecté). Plan action 180j: refonte formulaires consentements (3 mois), EIVP projets en cours (2 mois), processus demandes structuré ServiceNow (1 mois). Post-action: conformité 78% → 96%. Coût mise conformité: 28k$ vs amende potentielle 500k$-25M$ (ROI massif).",
    impactMetier: "Conformité 78% → 100%, Amendes évitées (25M$ potentiel), Réputation protégée, Processus structurés, Coût 28k$ ROI",
    pilier: 2,
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 5,
    raci: {
      responsible: "Directeur TI BZ",
      accountable: "Direction générale",
      consulted: "Avocat externe",
      informed: "Tous employés"
    },
    livrables: ["Registre conformité", "Tableau suivi obligations", "Rapport annuel"],
    livrablesVisuels: [
      {
        nom: "Registre Conformité Loi 25/RGPD",
        type: "Document",
        description: "Excel 45 obligations: statut conformité 96%, actions en cours, échéances, responsables, risques",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Conformité Légale",
        type: "Dashboard",
        description: "Métriques: conformité 96%, obligations couvertes 43/45, délai moyen actions 32j, alertes",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% obligations couvertes: 78% (cible: 100%) | Délai mise en conformité: 45j"
  },

  // PILIER 3 : Projets et planification (10 tactiques)
  {
    id: "P3-T01",
    nom: "Planifier projets annuels avec budgets",
    pilier: 3,
    description: "Anticiper besoins et sécuriser financement",
    descriptionDetaillee: "Roadmap annuelle projets TI anticipe besoins 12-18 mois, sécurise financement, évite navigation vue. Processus: T4 année N collecte besoins départements (rencontres directeurs), priorisation matrice valeur/risque/dépendances, construction roadmap 4 trimestres, budgétisation détaillée (CAPEX/OPEX), validation CFO/direction, présentation comité TI. Format: Gantt chart montrant 12-15 projets planifiés, dépendances, jalons clés, ressources, budgets trimestriels. Roadmap vivante: ajustements trimestriels basés réalités (projets ajoutés/reportés). Planning discipliné réduit firefighting réactif, améliore respect délais/budgets, et démontre professionnalisme direction.",
    casUsage: "TechnoSolutions roadmap 2024 planifie 12 projets (budget total 385k$): T1: upgrade VPN (35k$), formation cybersécurité (8k$). T2: CRM mobile (45k$), remplacements matériel (18k$). T3: IAM/SSO phase 1 (55k$), automatisation rapports (22k$). T4: infrastructure cloud scaling (85k$), audits sécurité (15k$). Budget sécurisé janvier 2024 évite blocages financement. Réalisation: 10/12 projets livrés (83%), 2 reportés 2025 (changements priorités business). Performance vs industrie (moyenne 60-65% projets planifiés livrés): excellence +25%.",
    impactMetier: "Projets planifiés livrés 80% → 90%, Budget sécurisé, Firefighting -65%, Prévisibilité accrue, Performance +25% vs industrie",
    priorite: "Haute",
    statut: "Fait",
    asWas: 1,
    asIs: 4,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "CFO", informed: "Équipes" },
    livrables: ["Calendrier annuel projets", "Budget détaillé", "Validation direction"],
    livrablesVisuels: [
      {
        nom: "Roadmap Projets 2024",
        type: "Présentation",
        description: "Gantt chart 12 projets sur 4 trimestres, dépendances, budgets 385k$, jalons clés, ressources",
        imageUrl: "https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?w=800&auto=format&fit=crop"
      },
      {
        nom: "Budget Projets Détaillé",
        type: "Document",
        description: "Excel breakdown: 12 projets, CAPEX/OPEX, répartition trimestrielle, validation CFO/direction",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets planifiés réalisés: 80% (cible: 90%)"
  },
  {
    id: "P3-T02",
    nom: "Suivre avancement et écarts budget/délais",
    pilier: 3,
    description: "Détecter dérives et actions correctives",
    descriptionDetaillee: "Suivi rigoureux projets détecte dérives AVANT catastrophe. Dashboard temps réel (PRIsME, Jira, MS Project) track 12 projets: status RAG (Red/Amber/Green), % completion, écarts budget (prévu vs réel), écarts délais (planning vs actuel), risques identifiés, prochaines actions. Rapport mensuel Directeur TI → Comité documente: progrès significatifs, problèmes rencontrés, décisions requises, prévisions achèvement révisées. Méthode Earned Value Management (EVM) calcule indices: CPI (Cost Performance Index = coût réel vs budgété), SPI (Schedule Performance Index = avancement réel vs planifié). Seuils alerte: écart budget >10% = escalade CFO, écart délai >15% = escalade direction.",
    casUsage: "TechnoSolutions projet CRM mobile suivi mensuel révèle: Mois 2 dérive délai (SPI 0.85 = 15% retard) due complexité intégration ERP sous-estimée. Action corrective immédiate: ajout ressource développeur senior 3 semaines (coût additionnel 4,5k$), priorisation user stories critiques, report fonctions nice-to-have phase 2. Mois 3: SPI récupère 0.98, projet livré mois 4.2 vs 4 planifié (+5% délai acceptable). Sans suivi rigoureux: dérive aurait continué, projet livré mois 6 (+50% dépassement). Moyenne 12 projets: 75% dans délais, 82% dans budget (vs industrie 50%/60%).",
    impactMetier: "Projets dans délais 75% → 85%, Dans budget 82% → 90%, Dérives détectées/corrigées tôt, Visibilité temps réel",
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Comité TI", consulted: "Chefs projet", informed: "Direction" },
    livrables: ["Rapports mensuels", "Tableaux écarts", "Dashboard PRIsME"],
    livrablesVisuels: [
      {
        nom: "Dashboard Portefeuille Projets",
        type: "Dashboard",
        description: "Temps réel: 12 projets RAG status, % completion, écarts budget/délais, CPI/SPI, alertes",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Mensuel Projets",
        type: "Rapport",
        description: "Document 15 pages: progrès, écarts, risques, décisions requises, prévisions achèvement, actions",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets dans délais: 75% | dans budget: 82%"
  },
  {
    id: "P3-T03",
    nom: "Évaluer ROI ou valeur d'affaires livrée",
    pilier: 3,
    description: "Démontrer que investissements produisent valeur",
    descriptionDetaillee: "ROI projets TI justifie investissements et démontre création valeur vs perception 'centre coûts'. Méthodologie: 1) PRE-projet: estimation ROI business case (coûts investissement vs bénéfices attendus 3 ans, seuil acceptation ROI >100% ou payback <24 mois). 2) POST-projet (6-12 mois post-déploiement): mesure bénéfices RÉELS vs prévus. Bénéfices tangibles: revenus additionnels, coûts réduits, temps économisé (valorisé $/h). Bénéfices intangibles: satisfaction client améliorée, qualité accrue, risques réduits (valorisation estimée). Rapport ROI post-projet compare prévu vs réel, explique écarts, lessons learned. Target: 85%+ projets ROI positif.",
    casUsage: "TechnoSolutions projet CRM mobile: Business case prévu ROI +165% sur 3 ans (investissement 45k$, bénéfices 120k$/an = revenus +80k$ nouveaux clients + productivité commerciale +40k$). Mesure 12 mois post: bénéfices réels 112k$/an (93% prévisions). ROI réalisé: +149% sur 3 ans (vs +165% prévu). Écart expliqué: adoption plus lente que prévu (6 mois vs 3 mois), mais bénéfices confirmés. Portfolio 12 projets: 9 ROI positif (75%), 2 neutres, 1 négatif. Moyenne ROI +165%. Communication direction: projets TI créent 385k$ valeur annuelle (ROI portfolio massif).",
    impactMetier: "Projets ROI positif 70% → 85%, Valeur créée 385k$/an, Justification investissements objective, Perception TI créateur valeur",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "CFO", consulted: "Directions", informed: "Comité TI" },
    livrables: ["Rapports ROI post-projet", "Analyse valeur", "Bénéfices tangibles"],
    livrablesVisuels: [
      {
        nom: "Rapport ROI Projet CRM",
        type: "Rapport",
        description: "Analyse 12 pages: business case, bénéfices prévus vs réels 112k$/an, ROI +149%, leçons apprises",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard ROI Portfolio",
        type: "Dashboard",
        description: "Métriques: 12 projets, 75% ROI+, valeur totale créée 385k$/an, ROI moyen +165%, graphiques",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets ROI positif: 70% (cible: 85%)"
  },
  {
    id: "P3-T04",
    nom: "Bilans post-projets (leçons apprises)",
    pilier: 3,
    description: "Capitaliser apprentissages et améliorer performance",
    descriptionDetaillee: "Post-mortem projets capitalise apprentissages évitant répéter erreurs. Réunion 2h équipe projet 2-4 semaines post-déploiement, facilitée Directeur TI. Questions structurées: Qu'avons-nous bien fait? (à répéter), Qu'avons-nous mal fait? (à éviter), Qu'avons-nous appris? (knowledge capture), Que ferions-nous différemment? (amélioration continue). Atmosphère blameless: focus processus pas personnes, safe space partage honnête. Rapport 5-8 pages documente insights, archivé PRIsME registre leçons (searchable). Analytics: catégorisation leçons (estimation, communication, technique, ressources), identification patterns récurrents (ex: sous-estimation intégrations systèmes = leçon répétée 3 projets). Target: 100% projets avec bilan.",
    casUsage: "TechnoSolutions projet CRM mobile post-mortem révèle leçons: BIEN - communication hebdo sponsor, méthodologie agile flexible. MAL - sous-estimation intégration ERP (2 semaines estimé, 5 réel), tests UAT démarrés tard. APPRIS - intégrations systèmes complexes nécessitent buffer 100-150%, UAT doit commencer parallèle dev pas après. Actions: template estimation intégrations révisé, checklist UAT précoce ajoutée standards. Leçons appliquées projet suivant (automatisation rapports): estimation intégrations précise, UAT précoce, livraison exactement selon planning. Bilans réalisés: 60% → 95% projets (cible 100%).",
    impactMetier: "Bilans 60% → 100%, Leçons capitalisées évitant erreurs répétées, Performance projets +22%, Culture amélioration continue",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 2,
    target: 4,
    raci: { responsible: "Chef projet BZ", accountable: "Directeur TI", consulted: "Équipe projet", informed: "Comité TI" },
    livrables: ["Rapports post-mortem", "Registre leçons", "Plan amélioration"],
    livrablesVisuels: [
      {
        nom: "Rapport Post-Mortem CRM",
        type: "Rapport",
        description: "Document 8 pages: succès, échecs, leçons apprises, actions amélioration, timeline projet",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      },
      {
        nom: "Registre Leçons PRIsME",
        type: "Document",
        description: "Base centralisée: 42 leçons archivées 12 projets, catégorisation, patterns identifiés, searchable",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets avec bilan: 60% (cible: 100%)"
  },
  {
    id: "P3-T05",
    nom: "Prioriser projets selon valeur/risques",
    pilier: 3,
    description: "Investir dans projets à fort impact",
    descriptionDetaillee: "Priorisation objective projets évite décisions politiques/émotionnelles. Matrice 2x2 Valeur (axe Y: basse/haute) vs Risque (axe X: bas/élevé) positionne 15-20 demandes projets annuelles. Valeur = combinaison ROI financier (revenus, économies) + valeur stratégique (alignement priorités, avantage compétitif) + urgence business. Risque = complexité technique, dépendances externes, disponibilité ressources, incertitudes périmètre. Scoring 1-10 chaque dimension, comité TI valide. Quadrants: Haute valeur/Bas risque = GO priorité 1, Haute valeur/Haut risque = GO avec mitigation, Basse valeur/Bas risque = backlog, Basse valeur/Haut risque = NO-GO. Réallocation ressources projets faible valeur vers haute valeur maximise ROI portfolio.",
    casUsage: "TechnoSolutions 18 demandes projets 2024 budget limité 385k$ (vs 520k$ demandes totales). Matrice priorisation: 5 projets quadrant haute valeur/bas risque financés immédiatement (CRM mobile, VPN upgrade, IAM SSO, automatisation rapports, remplacements matériel) = total 175k$. 3 projets haute valeur/haut risque financés avec mitigation (cloud scaling avec POC préalable, audits sécu avec experts externes, formation cyber structurée) = 155k$. 7 projets basse valeur reportés 2025 (portail employés, refonte intranet, outils collaboration redondants). 3 projets basse valeur/haut risque rejetés. Résultat: 85% budget investi projets haute valeur vs 45% sans priorisation structurée.",
    impactMetier: "Projets haute valeur financés 85% → 95%, Décisions objectives vs politiques, ROI portfolio maximisé +40%, Gaspillage éliminé",
    priorite: "Haute",
    statut: "Fait",
    asWas: 1,
    asIs: 4,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Comité TI", consulted: "Directions", informed: "Équipes" },
    livrables: ["Matrice valeur/risque", "Classement projets", "Validation comité"],
    livrablesVisuels: [
      {
        nom: "Matrice Valeur/Risque Projets",
        type: "Matrice",
        description: "Grille 2x2: 18 projets positionnés, quadrants colorés, décisions GO/NO-GO, budgets",
        imageUrl: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Priorisation Projets",
        type: "Rapport",
        description: "Document 10 pages: scoring 18 projets, rationale décisions, budget alloué 385k$, projets reportés/rejetés",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets haute valeur financés: 85%"
  },
  {
    id: "P3-T06",
    nom: "Maintenir portefeuille projets TI (PRIsME)",
    pilier: 3,
    description: "Vision globale avancement tous projets",
    descriptionDetaillee: "Portefeuille PRIsME = single source of truth tous projets TI. Plateforme centralisée (Notion, Jira Portfolio, MS Project Online, Monday.com) liste TOUS projets: actifs (12), planifiés (8), terminés archive (24). Fiche projet standardisée: titre, sponsor, budget, délai, status RAG, % completion, risques, prochaines actions, liens documents (cahier charge, rapports mensuels). Vue dashboard executif: répartition budgets par trimestre/catégorie, capacité équipe (heures disponibles vs engagées), alerte surcharge ressources. Mises à jour hebdomadaires chefs projet (15 min). Transparence totale: direction/parties prenantes accès lecture temps réel. Portefeuille élimine questions 'Où en est projet X?' - info accessible 24/7.",
    casUsage: "TechnoSolutions PRIsME portefeuille lancé T1 2024. Contient: 12 projets actifs, 8 planifiés roadmap, 24 terminés historique. Bénéfices: Direction consulte dashboard projet CRM mobile mercredi matin, voit status Green 85% completion, prochaine action UAT semaine prochaine, zéro question Directeur TI (gains temps 2h/semaine réunions status évitées = 100h/an économisées). CFO exporte rapport budget projets trimestriel Excel 3 clics vs 2 jours compilation manuelle pré-PRIsME. Parties prenantes notifiées automatiquement jalons atteints. 100% projets dans portefeuille, MAJ hebdo 100% respect.",
    impactMetier: "Visibilité 100% projets temps réel, Transparence totale parties prenantes, Temps réunions status -80%, Confiance direction accrue",
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 5,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Chefs projet", informed: "Toute entreprise" },
    livrables: ["Portefeuille PRIsME", "Tableaux suivi", "Mises à jour hebdo"],
    livrablesVisuels: [
      {
        nom: "Plateforme PRIsME Portefeuille",
        type: "Outil",
        description: "Interface centralisée: 12 projets actifs, 8 planifiés, dashboard executif, MAJ hebdo, accès parties prenantes",
        imageUrl: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Executif Projets",
        type: "Dashboard",
        description: "Vue consolidée: budgets 385k$, capacité équipe, timeline projets, RAG status, alertes, KPI",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets dans portefeuille: 100% | Fréquence MAJ: hebdo"
  },
  {
    id: "P3-T07",
    nom: "Comité gouvernance projets et TI",
    pilier: 3,
    description: "Structurer prise de décision et valider priorités",
    descriptionDetaillee: "Comité TI = organe gouvernance formelle décisions projets/TI stratégiques. Identique P1-T06 mais focus projets: membres (DG, CFO, directeurs départements, Directeur TI), fréquence trimestrielle 2-3h, agenda standardisé. Décisions clés: validation nouveaux projets (GO/NO-GO basé business case), arbitrage conflits ressources, ajustements budgets/roadmap, résolution blocages inter-départements, approbation changements majeurs périmètre/budget. Charte comité définit: mandat, membres, processus décision (consensus vs vote majorité), quorum, escalade désaccords. Procès-verbaux formels archive décisions traçabilité. Comité ancre TI gouvernance corporative, légitime décisions, et aligne parties prenantes.",
    casUsage: "TechnoSolutions comité TI T3 2024 exemple décisions: 1) GO projet CRM mobile (business case ROI +165%, budget 45k$ approuvé unanimité), 2) NO-GO portail employés (valeur faible vs coût 32k$, vote 6-1), 3) Ajustement roadmap: priorisation cybersécurité NIST post-audit révélant lacunes (réallocation budget 25k$ projet refonte intranet → audits sécu), 4) Résolution conflit: projet automatisation rapports nécessitant 40h département Finance (accord sponsor Finance libère ressource analyste 2 semaines). Taux participation comité: 95% (18/19 rencontres membres présents). Décisions comité respectées 100%, zéro contestation post-réunion.",
    impactMetier: "Gouvernance structurée 4/4 comités, Décisions légitimées, Taux participation 95%, Alignement parties prenantes, Zéro contestations",
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 5,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction générale", consulted: "Directions", informed: "Employés" },
    livrables: ["Charte comité", "Calendrier rencontres", "PV réguliers"],
    livrablesVisuels: [
      {
        nom: "Charte Comité TI",
        type: "Document",
        description: "Document formel 6 pages: mandat, membres, processus décision, quorum, calendrier annuel, escalade",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Procès-Verbal Comité T3",
        type: "Document",
        description: "PV 8 pages: 4 décisions GO/NO-GO, ajustements roadmap, résolutions conflits, actions, RACI",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Rencontres/an: 4 (100% respect) | Taux participation: 95%"
  },
  {
    id: "P3-T08",
    nom: "Système scoring (valeur, risque, dépendances)",
    pilier: 3,
    description: "Méthode objective comparaison projets",
    descriptionDetaillee: "Scoring quantitatif projets rend priorisation objective reproductible. Grille Excel scoring 3 dimensions pondérées: 1) VALEUR (50% poids) - ROI financier quantifié (0-10), alignement stratégique (0-10), urgence business (0-10), score = moyenne pondérée. 2) RISQUE (30% poids) - complexité technique (0-10), dépendances externes (0-10), certitude périmètre (0-10, inversé). 3) DÉPENDANCES (20% poids) - prérequis autres projets, ressources critiques limitées, fenêtres temporelles contraintes. Score final: (Valeur x 0.5) + (1-Risque x 0.3) + (Dépendances x 0.2) = score 0-10. Classement décroissant projets, seuil GO: score ≥7. Calibration annuelle pondérations basée retours comité TI.",
    casUsage: "TechnoSolutions scoring 18 demandes projets 2024. Top scores: CRM mobile 8.9 (valeur 9.5, risque bas 2, dépendances mineures) = GO priorité 1. VPN upgrade 8.7, IAM SSO 8.3 = GO. Scores moyens 6-7: cloud scaling 6.8 (valeur 8, risque élevé 6) = GO avec mitigation POC. Bas scores <5: portail employés 4.2 (valeur faible 4, risque moyen 5) = reporté. Transparence scoring élimine débats politiques: décisions basées méthodologie objective validée comité. 85% projets scorés → 100% (cible atteinte). Satisfaction parties prenantes processus: 8.5/10 (équitable, transparent, compréhensible).",
    impactMetier: "Projets scorés 85% → 100%, Décisions objectives documentées, Débats politiques éliminés, Satisfaction processus 8.5/10",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "Comité TI", consulted: "Directions", informed: "Chefs projet" },
    livrables: ["Grille scoring", "Scores par projet", "Classement consolidé"],
    livrablesVisuels: [
      {
        nom: "Grille Scoring Projets",
        type: "Document",
        description: "Excel template: 3 dimensions pondérées, formules automatisées, scoring 0-10, classement auto, seuils décision",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      },
      {
        nom: "Tableau Classement Projets",
        type: "Matrice",
        description: "Ranking 18 projets: scores finaux 4.2-8.9, valeur/risque/dépendances détaillés, décisions GO/NO-GO",
        imageUrl: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets scorés: 85% (cible: 100%)"
  },
  {
    id: "P3-T09",
    nom: "Revue semestrielle portefeuille (stop/start/continue)",
    pilier: 3,
    description: "Gouvernance agile - décider quels projets poursuivre",
    descriptionDetaillee: "Revue portefeuille semestrielle = gouvernance agile adaptant stratégie vs rigidité plan annuel figé. Analyse TOUS projets actifs (12) vs réalités business/techniques évoluées 6 mois. Décisions possibles: STOP (arrêt immédiat projet devenu non-pertinent, libération ressources), START (lancement nouveau projet urgence business émergée), CONTINUE (poursuite projets on-track), PIVOT (ajustement périmètre/objectifs). Critères décision: ROI actuel vs initial, changements priorités business, contraintes ressources/budget, feedbacks utilisateurs pilotes, opportunités technologiques nouvelles. Rapport 15 pages documente rationale décisions. Implémentation décisions rapide <2 semaines maximise agilité organisationnelle.",
    casUsage: "TechnoSolutions revue S2 2024 portefeuille 12 projets actifs: CONTINUE 9 projets on-track. PIVOT 1 projet: automatisation rapports périmètre élargi inclure département Finance (demande émergée, budget additionnel 8k$ approuvé). STOP 1 projet: refonte intranet (priorité baissée, utilisateurs satisfaits version actuelle post-formation, ressources réallouées cybersécurité NIST prioritaire). START 1 projet: intégration outil BI nouveau (opportunité fournisseur discount 40% si démarrage avant décembre). 25% projets ajustés (3/12), décisions implémentées 12 jours. Agilité portefeuille démontrée vs plan rigide annuel.",
    impactMetier: "Agilité portefeuille 25% ajustements, Arrêt projets non-pertinents libérant ressources, Opportunités saisies, Délai implémentation 12j",
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 4,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Comité TI", informed: "Équipes" },
    livrables: ["Rapport semestriel", "Décisions stop/start", "Plan action MAJ"],
    livrablesVisuels: [
      {
        nom: "Rapport Revue Portefeuille S2",
        type: "Rapport",
        description: "Document 15 pages: analyse 12 projets, 3 décisions stop/start/pivot, rationale, plan action, timeline",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      },
      {
        nom: "Matrice Stop/Start/Continue",
        type: "Matrice",
        description: "Grille décisions: 9 continue, 1 pivot, 1 stop, 1 start, impact ressources, budgets réalloués",
        imageUrl: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets ajustés lors revue: 25% | Délai implémentation: 2 sem"
  },
  {
    id: "P3-T10",
    nom: "Intégrer gestion risques projets",
    pilier: 3,
    description: "Réduire chances dérives majeures",
    descriptionDetaillee: "Gestion risques projets proactive réduit dérives catastrophiques. Registre risques Excel/Jira track TOUS risques identifiés projet: description risque, probabilité (faible/moyen/élevé), impact si matérialise (faible/moyen/élevé), score risque (probabilité x impact = priorité 1-9), stratégie mitigation (éviter, réduire, transférer, accepter), actions mitigation spécifiques, responsable, statut suivi. Identification risques: brainstorming équipe kick-off projet, analyse past lessons learned projets similaires, consultation experts domaine. Revue risques hebdomadaire réunion statut projet. Risques matérialisés = incidents documentés, actions correctives, lessons learned. Target: 100% projets avec registre risques actif.",
    casUsage: "TechnoSolutions projet CRM mobile registre risques identifie 12 risques initiaux. Risque critique #3: 'Complexité intégration ERP sous-estimée' (probabilité haute, impact élevé, score 9/9). Mitigation: analyse technique approfondie pré-dev (2 semaines), consultation expert ERP externe (3k$), buffer planning +20%. Risque matérialise partiellement: intégration plus complexe que prévu malgré mitigation, mais buffer absorbe retard, projet livré +5% délai vs +50% sans mitigation. Registre final: 12 risques identifiés, 3 matérialisés, 9 évités/réduits. Projets avec registre: 70% → 95% (cible 100% proche). Dérives majeures projets -60% vs projets sans registre.",
    impactMetier: "Projets avec registre 70% → 100%, Dérives majeures -60%, Risques identifiés/mitigés proactivement, Culture gestion risques",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: { responsible: "Chef projet BZ", accountable: "Directeur TI", consulted: "Experts", informed: "Comité TI" },
    livrables: ["Registre risques", "Plan mitigation", "Suivi incidents"],
    livrablesVisuels: [
      {
        nom: "Registre Risques Projet CRM",
        type: "Document",
        description: "Excel 12 risques: probabilité/impact scores, stratégies mitigation, actions, responsables, statut, timeline",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Risques Portefeuille",
        type: "Dashboard",
        description: "Vue consolidée: 12 projets, 85 risques totaux, heatmap criticité, risques matérialisés, tendances",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets avec registre risques: 70% (cible: 100%)"
  },

  // PILIER 4 : Opérations & Support (10 tactiques abrégées)
  {
    id: "P4-T01",
    nom: "Mesurer temps moyen résolution tickets",
    pilier: 4,
    description: "KPI central performance support TI",
    descriptionDetaillee: "Le temps moyen résolution (Mean Time To Resolve - MTTR) mesure efficacité support. Outil ticketing (Jira Service Management, Freshdesk, Zendesk) capture : ouverture ticket, premier contact, résolution, fermeture. Catégorisation par type (mot de passe, réseau, matériel, logiciel, accès) et priorité (P1-critique, P2-haute, P3-normale, P4-basse). Dashboard temps réel montre : MTTR global 4.2h, MTTR par catégorie, % respect SLA 92%, tendances évolution. Alertes automatiques si dépassements SLA. Analyse mensuelle identifie goulots et opportunités amélioration (base connaissance, formation, automatisation).",
    casUsage: "TechnoSolutions identifie via analyse : 35% tickets = réinitialisation mot de passe (MTTR: 6h). Action: déploiement portail self-service. Résultat: tickets mot de passe -70%, MTTR global passe 4.2h → 3.1h, satisfaction +18%.",
    impactMetier: "Productivité +15% (moins temps perdu), Satisfaction CSAT 84%, Coûts support -25%",
    priorite: "Haute",
    statut: "Fait",
    asWas: 3,
    asIs: 4,
    target: 5,
    raci: { responsible: "Support TI BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Rapports mensuels", "Dashboard PRIsME", "Temps par catégorie"],
    livrablesVisuels: [
      {
        nom: "Dashboard Support Temps Réel",
        type: "Dashboard",
        description: "Métriques live : MTTR 4.2h, tickets ouverts 23, SLA respect 92%, graphiques tendances",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Performance Mensuel",
        type: "Rapport",
        description: "Analyse 12 pages : volumes tickets, MTTR par catégorie, satisfaction, top irritants, recommandations",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Temps moyen: 4.2h (cible: <4h) | % respect SLA: 92%"
  },
  {
    id: "P4-T02",
    nom: "Suivre taux résolution premier contact",
    pilier: 4,
    description: "Indicateur maturité et compétence support",
    descriptionDetaillee: "Taux résolution premier contact (First Contact Resolution - FCR) mesure % tickets résolus sans escalade/réouverture. FCR élevé = support compétent, documentation adéquate, outils efficaces. Métrique: tickets fermés première interaction / tickets totaux x 100. Facteurs impactant FCR: formation techniciens (knowledge skills), accès outils diagnostic (remote control, logs), base connaissances riche, autorité résoudre sans approbations multiples. Dashboard track FCR global + FCR par technicien (identification besoins formation individuelle) + FCR par catégorie (gaps documentation). Target industrie: 70-80% FCR. Amélioration FCR réduit coûts (moins temps techniciens) et améliore satisfaction (résolution rapide sans callbacks frustrants).",
    casUsage: "TechnoSolutions FCR initial 68% analysé T2 2024. Détails: catégories FCR élevé: mot de passe 92%, VPN 78% (procédures simples, bien documentées). FCR faible: imprimantes 45%, problèmes réseau 52% (complexité, manque outils diagnostic). Actions: formation avancée troubleshooting réseau 2 jours (coût 1.2k$), achat logiciel diagnostic réseau (3k$), enrichissement base connaissances imprimantes 12 articles. Résultat 6 mois post: FCR global 68% → 76% (cible 75%+ atteinte). FCR imprimantes 45% → 68%, réseau 52% → 71%. Satisfaction utilisateurs résolution rapide +22%, coûts support temps techniciens -18% (moins escalades/réouvertures).",
    impactMetier: "FCR 68% → 76%, Satisfaction résolution rapide +22%, Coûts support temps -18%, Formation ciblée efficace",
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: { responsible: "Support TI BZ", accountable: "Directeur TI", consulted: "Équipe support", informed: "Utilisateurs" },
    livrables: ["Rapports mensuels", "Suivi automatisé", "Score satisfaction"],
    livrablesVisuels: [
      {
        nom: "Dashboard FCR Temps Réel",
        type: "Dashboard",
        description: "Métriques: FCR global 76%, FCR par technicien, FCR par catégorie imprimantes 68%/réseau 71%, tendances",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Amélioration FCR",
        type: "Rapport",
        description: "Analyse 10 pages: FCR initial 68%, actions formation/outils 4.2k$, résultats +8 points, ROI",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Taux résolution 1er contact: 68% (cible: 75%+)"
  },
  {
    id: "P4-T03",
    nom: "Surveiller disponibilité systèmes critiques",
    pilier: 4,
    description: "Indicateur vital continuité affaires",
    descriptionDetaillee: "Uptime (disponibilité) systèmes critiques = métrique business continuité. Systèmes critiques: ERP (commandes, facturation, inventaire), email (communication), CRM (ventes), serveurs fichiers (documents), réseau internet. Monitoring 24/7 outils (Nagios, PRTG, Datadog, Azure Monitor) ping services chaque 1-5 min, alertes automatiques si down. Calcul uptime: (temps total - temps arrêt) / temps total x 100. Target industrie: 99.5% = 3.65h arrêt maximum/mois, 99.9% = 43 min/mois. Dashboard temps réel affiche: uptime % systèmes, incidents actifs, MTTR (Mean Time To Restore) moyen, historique pannes. Rapports mensuels SLA direction documentent respect engagements disponibilité.",
    casUsage: "TechnoSolutions uptime 2024 systèmes critiques: ERP cloud 99.8% (incident majeur septembre 2h10), email Office 365 99.95% (Microsoft SLA garanti), CRM cloud 99.7%, réseau internet 99.6% (panne fournisseur 2 incidents 3h cumulées). Uptime global pondéré: 99.7% (cible 99.5% dépassée). MTTR incidents: ERP 2h10 (incident majeur), autres <45min moyenne. Monitoring Datadog alerte automatique SMS/email on-call technicien, réponse moyenne 8 minutes. Bénéfices monitoring proactif: détection panne serveur fichiers 4h du matin AVANT arrivée employés, résolution proactive 45 min, zéro impact productivité vs découverte 8h = 85 employés bloqués.",
    impactMetier: "Uptime 99.7% (cible 99.5%+), MTTR 45min, Détection proactive 8 min, Zéro impact productivité incidents nuit",
    priorite: "Haute",
    statut: "Fait",
    asWas: 3,
    asIs: 5,
    target: 5,
    raci: { responsible: "Technicien TI BZ", accountable: "Directeur TI", consulted: "Fournisseurs", informed: "Tous employés" },
    livrables: ["Dashboard monitoring", "Rapports disponibilité", "Alertes auto"],
    livrablesVisuels: [
      {
        nom: "Dashboard Monitoring Temps Réel",
        type: "Dashboard",
        description: "Vue live: uptime ERP 99.8%, email 99.95%, CRM 99.7%, status systèmes green/red, alertes actives",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Disponibilité Mensuel",
        type: "Rapport",
        description: "Document 8 pages: uptime % systèmes, incidents détails, MTTR, impact business, actions préventives",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Disponibilité: 99.7% (cible: 99.5%+) | MTTR: 45min"
  },
  {
    id: "P4-T04",
    nom: "Mettre à jour régulièrement SLA",
    pilier: 4,
    description: "Évoluer avec besoins et attentes utilisateurs",
    descriptionDetaillee: "SLA (Service Level Agreements) vivants évoluent vs figés obsolètes. Revue annuelle processus: 1) Analyse performance actuelle (respect SLA 92%, gaps identifiés P3/P4 sous-cibles). 2) Collecte feedbacks utilisateurs (sondage satisfaction, interviews directeurs départements, analyse tickets). 3) Benchmark industrie (SLA concurrents, best practices ITIL). 4) Proposition ajustements (délais, priorités, services couverts, heures support). 5) Validation comité TI et direction. 6) Communication employés (email all-hands, intranet, formations). Exemples ajustements: relèvement délais P4 (5j → 7j libérant capacité P1/P2 critiques), ajout nouveau service support mobile (BYOD émergent), extension heures support 8h-18h → 7h-19h.",
    casUsage: "TechnoSolutions revue SLA annuelle T4 2024. Performance actuelle: P1 96%, P2 92%, P3 88%, P4 78% (P4 sous-cible 85%). Feedbacks: directeurs satisfaits P1/P2, frustrations P4 délais non-respectés, demandes support mobile BYOD croissantes. Décisions: 1) P4 délais relevés 5j → 7j (réaliste capacité), 2) Nouveau service 'Support appareils mobiles personnels' ajouté catalogue (heures limitées 10h-16h, best-effort), 3) P1 délais réduits 2h → 1h30 (amélioration continue). SLA révisé 100%, communication 3 canaux (email, intranet, lunch-and-learn). Adoption: taux connaissance SLA révisé 78% → 92%, respect P4 révisé 78% → 91% (délais réalistes).",
    impactMetier: "SLA révisés 100% annuellement, Délais réalistes respect P4 78% → 91%, Nouveaux services ajoutés, Connaissance SLA 92%",
    priorite: "Moyenne",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Utilisateurs", informed: "Support" },
    livrables: ["Documentation SLA", "Validation annuelle", "Communication"],
    livrablesVisuels: [
      {
        nom: "Document SLA Révisé 2024",
        type: "Document",
        description: "SLA actualisé 14 pages: délais P1-P4 ajustés, nouveau service mobile, heures support, changelog",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Présentation Changements SLA",
        type: "Présentation",
        description: "Slides lunch-and-learn: 3 changements clés, rationale, impacts utilisateurs, Q&A",
        imageUrl: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% SLA révisés annuellement: 100% | Taux respect: 92%"
  },
  {
    id: "P4-T05",
    nom: "Améliorer base connaissance support",
    pilier: 4,
    description: "Réduire dépendance support et améliorer autonomie",
    descriptionDetaillee: "Base connaissances vivante croît continuellement basée analytics tickets. Processus amélioration: 1) IDENTIFICATION gaps - analyse trimestrielle tickets récurrents (ex: 'Configurer signature Outlook' = 18 tickets/trimestre, aucun article existant). 2) CRÉATION contenu - rédaction article step-by-step screenshots par technicien ayant résolu, revue pair, publication. 3) PROMOTION - lien article ajouté réponses tickets similaires futurs, mention newsletter TI mensuelle, formation nouveaux employés. 4) MAINTENANCE - revue semestrielle articles obsolètes (logiciels changés versions), mises à jour/archivage. 5) ANALYTICS - tracking consultations articles (Google Analytics), ratings utilisateurs (utile yes/no), identification articles top/flop. Target: 45 articles nouveaux/an, 80% utilisateurs consultent base régulièrement.",
    casUsage: "TechnoSolutions base connaissances amélioration continue 2024. T1: 28 articles initiaux, taux utilisation 52%. Analyse révèle gaps: signature Outlook (18 tickets/trim), VPN Mac (12 tickets/trim), Teams partage écran (10 tickets/trim). T2-T4: création 45 nouveaux articles ciblant gaps, tutoriels vidéo 8 procédures complexes, FAQ enrichie 18 questions. Promotion: liens automatiques tickets (bot suggère articles), newsletter TI mensuelle highlight article du mois. Résultats 12 mois: articles totaux 73, consultations 1,240 → 2,850/trimestre (+130%), taux utilisation 52% → 78% (cible 80% proche). Tickets évitables signature Outlook -72%, VPN Mac -68%, Teams -55%. ROI: 285h support économisées/an ($14,250 valeur).",
    impactMetier: "Articles 28 → 73, Utilisation 52% → 78%, Consultations +130%, Tickets évitables -65% catégories ciblées, ROI $14k/an",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: { responsible: "Support TI BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Tous employés" },
    livrables: ["Articles mis à jour", "Tutoriels", "FAQ documentées"],
    livrablesVisuels: [
      {
        nom: "Base Connaissances Enrichie",
        type: "Outil",
        description: "Confluence: 73 articles, 8 vidéos, recherche améliorée, analytics 2850 consultations/trim, ratings",
        imageUrl: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Analytics Base Connaissance",
        type: "Rapport",
        description: "Dashboard: articles top 10, consultations +130%, taux utilisation 78%, gaps identifiés, ROI $14k",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Articles ajoutés: 45/an | Taux utilisation: 62% (cible: 80%)"
  },
  {
    id: "P4-T06",
    nom: "Processus ITIL (Incident, Change, Problem)",
    pilier: 4,
    description: "Standards reconnus pour qualité services TI",
    descriptionDetaillee: "ITIL 4 framework standards best practices gestion services TI. 3 processus clés: 1) INCIDENT Management - restaurer service normal rapidement (ticket → diagnostic → résolution temporaire → fermeture, KPI: MTTR). 2) CHANGE Management - gérer changements infrastructure/applications minimisant risques (demande → évaluation impact/risque → approbation CAB → implémentation → revue post, fenêtre maintenance, rollback plan). 3) PROBLEM Management - identifier root causes incidents récurrents éliminant définitivement (analyse patterns incidents, investigation approfondie, résolution permanente, leçons apprises). Processus documentés workflows, rôles RACI, templates, formation équipe. Audits qualité trimestriels mesure conformité 85-90% vs 100% rigidité excessive.",
    casUsage: "TechnoSolutions implémentation ITIL 4 T1-T2 2024. INCIDENT: workflow 5 étapes (détection → classification P1-P4 → diagnostic → résolution → fermeture), template fiche incident, MTTR tracking. CHANGE: CAB (Change Advisory Board) mensuel Directeur TI + tech senior + sponsor business, évaluation risque matrice probabilité/impact, fenêtres maintenance jeudi 18h-22h, rollback plans obligatoires. PROBLEM: registre problèmes track incidents récurrents (ex: 'Lenteur réseau bureaux sud' = 12 incidents 6 mois), investigation root cause (switch vétuste), résolution permanente (remplacement switch 2.5k$), incidents futurs éliminés. Conformité ITIL audit T4: 95% (target atteint). Maturité ITIL: niveau 4/5 (optimisé, amélioration continue).",
    impactMetier: "Conformité ITIL 95%, Maturité niveau 4/5, Incidents récurrents -60%, Changements risque minimisé, Professionnalisme accru",
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 5,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Support", informed: "Utilisateurs" },
    livrables: ["Procédures ITIL", "Outils configurés", "Formation équipe"],
    livrablesVisuels: [
      {
        nom: "Documentation Processus ITIL",
        type: "Document",
        description: "Manuel 45 pages: workflows incident/change/problem, templates, rôles RACI, KPI, exemples",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Audit Conformité ITIL",
        type: "Rapport",
        description: "Évaluation T4 2024: conformité 95%, maturité 4/5, forces, gaps, recommandations",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% processus ITIL implémentés: 100% | Conformité: 95%"
  },
  {
    id: "P4-T07",
    nom: "Former équipe support nouvelles technologies",
    pilier: 4,
    description: "Garder équipe à jour face évolutions technologiques",
    descriptionDetaillee: "Formation continue équipe support maintient compétence face évolutions rapides techno. Plan formation annuel identifie: nouvelles technologies déployées (Microsoft 365, Azure, outils cybersécurité), compétences gaps actuels (cloud, automation, troubleshooting avancé), certifications pertinentes (CompTIA A+/Network+, Microsoft, ITIL). Budget formation: 2-5% masse salariale TI (benchmark). Format: formations externes (2-3j intensives fournisseurs/écoles), webinaires en ligne (Udemy, Pluralsight, LinkedIn Learning), certifications payées employeur (examen + renouvellements), lunch-and-learn internes partage connaissances. Registre formations track: heures formées/employé, certifications obtenues, évaluations post-formation. Target: 80% équipe formée nouvelles techno annuellement, 12 certifications obtenues équipe/an.",
    casUsage: "TechnoSolutions plan formation 2024 budget 8,5k$ (3% masse salariale TI 3 personnes). Formations: Tech senior certification Azure Administrator (cours 5j 2,5k$ + examen 165$), Tech junior CompTIA A+ (cours en ligne 450$ + examen 250$), Directeur TI ITIL 4 Foundation refresh (webinaire 350$). Lunch-and-learn mensuels: tech senior partage nouvelles fonctions Microsoft 365, troubleshooting réseau avancé. Résultats: 100% équipe formée (cible 80% dépassée), 3 certifications obtenues (cible 12/an = 1/personne atteinte), compétences cloud Azure permettant migration infrastructure économisant 28k$/an hébergement local. ROI formation: 330% (investissement 8.5k$ = économies 28k$ première année).",
    impactMetier: "Équipe formée 80% → 100%, Certifications 3/3, Compétences cloud migration -$28k/an hébergement, ROI formation 330%",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 3,
    asIs: 4,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "RH", consulted: "Formateurs", informed: "Support" },
    livrables: ["Plan annuel formation", "Registre formations", "Certifications"],
    livrablesVisuels: [
      {
        nom: "Plan Formation Annuel 2024",
        type: "Document",
        description: "Budget 8.5k$, 3 employés, 5 formations planifiées, certifications cibles, calendrier, ROI",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      },
      {
        nom: "Registre Formations & Certifications",
        type: "Document",
        description: "Excel tracking: heures formées/personne, certifications obtenues 3/3, évaluations, investissement",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% équipe formée nouvelles techno: 80% | Certifications: 12/an"
  },
  {
    id: "P4-T08",
    nom: "Mesurer satisfaction utilisateurs (CSAT/NPS)",
    pilier: 4,
    description: "Reflet qualité perçue du support",
    descriptionDetaillee: "Satisfaction utilisateurs (CSAT) et Net Promoter Score (NPS) mesurent qualité perçue support. CSAT: sondage post-ticket automatique 'Comment évaluez-vous support reçu?' échelle 1-5, envoyé email 2h post-fermeture ticket. Calcul: (réponses 4-5 / réponses totales) x 100 = % satisfaction. Target: 80-90% CSAT. NPS: sondage trimestriel 'Recommanderiez-vous support TI collègue?' échelle 0-10. Promoteurs (9-10) - Détracteurs (0-6) = NPS. Target: NPS >40 bon, >70 excellent. Analyse qualitative commentaires open-ended identifie forces (rapidité, courtoisie) et gaps (manque follow-up, communication). Rapport trimestriel direction + plan action amélioration gaps prioritaires. Taux participation 70%+ valide statistiquement.",
    casUsage: "TechnoSolutions mesure satisfaction T1-T4 2024. CSAT post-ticket: 840 sondages envoyés, 605 réponses (72% participation), 507 réponses 4-5 → CSAT 84% (target 80%+ atteint). NPS trimestriel: 85 employés, 68 réponses (80%), 32 promoteurs (score 9-10), 28 passifs (7-8), 8 détracteurs (0-6) → NPS +35 (score bon, cible +45). Analyse commentaires: forces = rapidité résolution, courtoisie techniciens. Gaps = manque communication proactive statut tickets longs, frustration délais P4. Actions amélioration T1 2025: notifications automatiques statut tickets +3j (outils mise à jour), révision SLA P4 délais réalistes. Cible NPS +45 atteinte T2 2025 post-actions.",
    impactMetier: "CSAT 84% (target 80%+), NPS +35 → +45, Participation 72%, Gaps identifiés/actionnés, Amélioration continue",
    priorite: "Haute",
    statut: "Fait",
    asWas: 3,
    asIs: 4,
    target: 5,
    raci: { responsible: "Support TI BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Sondages post-tickets", "Rapport trimestriel", "Plan action"],
    livrablesVisuels: [
      {
        nom: "Dashboard Satisfaction Temps Réel",
        type: "Dashboard",
        description: "CSAT 84%, NPS +35, participation 72%, évolution trimestrielle, commentaires récents",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Satisfaction Trimestriel",
        type: "Rapport",
        description: "Document 12 pages: CSAT/NPS détails, commentaires analysés, forces/gaps, plan action 3 priorités",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "CSAT: 4.2/5 (84%) | NPS: +45 | Taux participation: 72%"
  },
  {
    id: "P4-T09",
    nom: "Automatiser reporting incidents et qualité",
    pilier: 4,
    description: "Réduire charge manuelle et favoriser décision rapide",
    descriptionDetaillee: "Automatisation reporting élimine compilation manuelle chronophage, accélère décisions data-driven. Outils ticketing (Jira, ServiceNow, Freshdesk) + BI (Power BI, Tableau) génèrent rapports automatiques quotidiens/hebdo/mensuels: volumes tickets par catégorie/priorité/technicien, MTTR, FCR, SLA respect, tendances, comparaisons périodes. Dashboard interactif temps réel direction/managers drill-down détails (ex: clic catégorie 'Réseau' montre 12 tickets actifs, détails chacun). Alertes automatiques anomalies (ex: spike tickets +40% vs moyenne = incident potentiel, SLA P1 manqué = escalade immédiate). Automatisation libère 8-12h/mois technicien vs reporting manuel Excel, temps réalloué support/projets.",
    casUsage: "TechnoSolutions automatisation reporting T2 2024 Power BI connecté Jira. Avant: tech junior compile Excel rapports manuels 10h/mois (volumes, MTTR, SLA, graphiques), délai production 3-5 jours post-mois. Après: rapports automatiques générés quotidien 6h AM (volumes veille, MTTR rolling 7j, SLA respect temps réel), tableau de bord direction accessible 24/7, alertes email automatiques anomalies (ex: spike tickets VPN +65% détecté, investigation proactive révèle panne fournisseur). Bénéfices: temps compilation -100% (10h/mois économisées = $500/mois valeur), délai production instant vs 3-5j (décisions 5x plus rapides), visibilité temps réel vs snapshot mensuel. % reporting automatisé: 85% (15% rapports ad-hoc personnalisés restent manuels).",
    impactMetier: "Reporting automatisé 85%, Délai production instant vs 3-5j, Temps économisé 10h/mois ($500), Visibilité temps réel, Décisions 5x rapides",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: { responsible: "Technicien TI BZ", accountable: "Directeur TI", consulted: "Support", informed: "Direction" },
    livrables: ["Rapports automatisés", "Dashboard interactif", "Alertes anomalies"],
    livrablesVisuels: [
      {
        nom: "Dashboard Power BI Support",
        type: "Dashboard",
        description: "Temps réel: volumes tickets, MTTR, FCR, SLA, tendances, drill-down détails, alertes anomalies",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Automatisé Mensuel",
        type: "Rapport",
        description: "PDF généré automatique: métriques clés, graphiques, comparaisons mois précédent, insights",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% reporting automatisé: 85% | Délai production: instant"
  },
  {
    id: "P4-T10",
    nom: "Plans continuité services critiques",
    pilier: 4,
    description: "Assurer services essentiels même en incident majeur",
    descriptionDetaillee: "Plan continuité services (Business Continuity Plan - BCP) garantit services critiques maintien minimum acceptable incident majeur (panne serveur, sinistre, cyberattaque). Identification services critiques RTO/RPO: ERP (RTO 4h = service rétabli <4h, RPO 1h = perte données max 1h), email (RTO 2h, RPO 15 min), CRM (RTO 6h, RPO 2h). Stratégies continuité: backups fréquents cloud, serveurs redondants haute disponibilité, procédures dégradées manuelles (ex: ERP down = saisie commandes papier temporaire). Plan documenté 30-50 pages: rôles responsables, procédures activation, contacts fournisseurs urgence, communication employés/clients. Tests simulation annuels (tabletop exercise scenario panne ERP, mesure temps activation). Cible: 100% services critiques couverts, 2 tests/an.",
    casUsage: "TechnoSolutions BCP élaboré T3 2024. Services critiques identifiés: ERP, email, CRM, serveurs fichiers, réseau. Stratégies: ERP cloud backup 4x/jour (RPO 6h réel vs 1h cible à améliorer), migration cloud Azure redondance géographique, email Office 365 SLA Microsoft 99.9%, procédures dégradées documentées (commandes papier, communication SMS si email down). Test simulation octobre 2024: scenario panne ERP ransomware. Résultats: plan activé 45 min vs 4h RTO (excellent), restauration backup cloud ERP 3h15, communication employés effectuée, gaps identifiés = procédure communication clients absente (ajoutée plan). Services couverts: 80% (4/5 critiques, réseau pas encore couvert BCP complet). Target 100% T2 2025.",
    impactMetier: "Services critiques couverts 80% → 100%, Tests simulation 2/an, RTO respecté 3h15 vs 4h cible, Gaps identifiés/corrigés",
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Fournisseurs", informed: "Tous employés" },
    livrables: ["Plan continuité documenté", "Exercices simulation", "Rapport audit"],
    livrablesVisuels: [
      {
        nom: "Plan Continuité Services (BCP)",
        type: "Document",
        description: "Manuel 42 pages: services critiques RTO/RPO, stratégies, procédures, rôles, contacts, tests",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Test Simulation BCP",
        type: "Rapport",
        description: "Résultats test octobre: scenario ransomware ERP, activation 45min, restauration 3h15, gaps, actions",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% services couverts: 80% (cible: 100%) | Tests/an: 2"
  },

  // PILIER 5 : Cybersécurité (6 tactiques alignées NIST CSF 2.0)
  {
    id: "P5-T01",
    nom: "Gouvernance",
    pilier: 5,
    description: "Établir et surveiller la stratégie de cybersécurité, la posture et les programmes",
    descriptionDetaillee: "La fonction GOVERN du NIST CSF 2.0 établit la fondation de toute stratégie cybersécurité. Elle comprend: 1) CONTEXTE ORGANISATIONNEL (GV.OC) - mission claire, objectifs alignés, culture sécurité intégrée. 2) STRATÉGIE GESTION RISQUES (GV.RM) - stratégie risques définie, tolérance au risque établie, décisions éclairées. 3) RÔLES & RESPONSABILITÉS (GV.RR) - rôles cybersécurité clairs, responsabilités définies, RACI documenté. 4) POLITIQUES (GV.PO) - politiques documentées, procédures établies, conformité assurée. 5) SURVEILLANCE (GV.OV) - surveillance gouvernance, audits réguliers, amélioration continue. Livrables: politique cybersécurité formelle, registre risques TI, rapport conformité NIST annuel, indice maturité cyber, heatmap conformité, plan amélioration. Target: 85%+ conformité NIST, indice maturité 4.2/5.",
    casUsage: "TechnoSolutions gouvernance cyber 2024. Audit NIST 2.0 novembre: Govern 72% (vs 85% cible). Écarts: politique cyber formelle absente, registre risques incomplet, surveillance gouvernance limitée. Actions: T1 2025 élaboration politique cyber 15 pages (mission, objectifs, rôles RACI, procédures), T2 registre risques complet (risques identifiés, priorités, plans mitigation), T3 surveillance trimestrielle comité TI cyber. Indice maturité cyber baseline: 3.1/5 (62%) → roadmap 4.2/5 (84%) 2026. Bénéfice: fondation solide gouvernance vs approche réactive pré-NIST.",
    impactMetier: "Conformité Govern 72% → 85%, Indice maturité 3.1/5 → 4.2/5, Politique cyber formelle, Registre risques complet, Surveillance gouvernance",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Comité TI", informed: "Tous employés" },
    livrables: ["Politique cybersécurité formelle", "Registre risques TI", "Rapport conformité NIST annuel", "Indice maturité cyber", "Heatmap conformité NIST", "Plan amélioration"],
    livrablesVisuels: [
      {
        nom: "Politique Cybersécurité Formelle",
        type: "Document",
        description: "Document 15 pages: mission, objectifs, rôles RACI, procédures, conformité NIST 2.0, surveillance gouvernance",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      },
      {
        nom: "Heatmap Conformité NIST",
        type: "Matrice",
        description: "Visuel 108 contrôles: rouge/jaune/vert, 6 domaines Govern-Recover, scores détaillés, progression",
        imageUrl: "https://images.unsplash.com/photo-1529078155058-5d716f45d604?w=800&auto=format&fit=crop"
      },
      {
        nom: "Spider Chart Maturité Cyber",
        type: "Dashboard",
        description: "Visuel 6 dimensions scores 2.5-3.8/5, indice global 3.1/5, comparaison 2023-2024, benchmark +15%",
        imageUrl: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Audit NIST 2.0",
        type: "Rapport",
        description: "Document 30 pages: score 68%, heatmap domaines, écarts critiques 8, roadmap priorisation 26.5k$",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Score Govern NIST: 72% (cible: 85%+) | Indice maturité: 3.1/5 (cible: 4.2/5)"
  },
  {
    id: "P5-T02",
    nom: "Identifier",
    pilier: 5,
    description: "Développer une compréhension organisationnelle pour gérer les risques de cybersécurité",
    descriptionDetaillee: "La fonction IDENTIFY du NIST CSF 2.0 établit la base de toute sécurité: on ne peut pas protéger ce qu'on ne connaît pas. Elle comprend: 1) GESTION ACTIFS (ID.AM) - inventaire complet actifs TI, classification données, propriétaires identifiés. 2) ENVIRONNEMENT D'AFFAIRES (ID.BE) - mission claire, objectifs définis, rôles établis. 3) GOUVERNANCE (ID.GV) - politiques documentées, procédures établies. 4) ÉVALUATION RISQUES (ID.RA) - risques identifiés, priorités établies, plans mitigation. 5) STRATÉGIE GESTION RISQUES (ID.RM) - stratégie définie, tolérance au risque. 6) GESTION RISQUES CHAÎNE APPROVISIONNEMENT (ID.SC) - fournisseurs évalués, contrats sécurisés, monitoring continu. Livrables: inventaire actifs TI complet, registre risques TI, évaluations sécurité fournisseurs (VRM), contrats clauses cyber, matrice risques fournisseurs. Target: 100% actifs inventoriés, 100% fournisseurs critiques évalués.",
    casUsage: "TechnoSolutions IDENTIFY 2024. Inventaire actifs: 85 postes, 12 serveurs, 45 applications SaaS, 18 fournisseurs TI. Couverture: 78% actifs inventoriés (gap 22% = postes anciens, applications shadow IT). VRM: 8 fournisseurs critiques, 40% évalués (3/8 = Microsoft SOC 2 validé, ERP/CRM évaluations cours). Gaps: 5 fournisseurs critiques non-évalués, inventaire incomplet. Roadmap T1-T2 2025: inventaire 100% (scan réseau automatisé, audit manuel), évaluations fournisseurs 100% (questionnaires standardisés, clauses contrats). Bénéfice: visibilité complète actifs/risques vs aveuglement pré-NIST.",
    impactMetier: "Inventaire actifs 78% → 100%, Fournisseurs critiques évalués 40% → 100%, Risques identifiés/mitigés, Contrats clauses cyber renforcées",
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "CFO", consulted: "Fournisseurs", informed: "Direction" },
    livrables: ["Inventaire actifs TI complet", "Registre risques TI", "Évaluations sécurité fournisseurs (VRM)", "Contrats clauses cyber", "Registre fournisseurs VRM", "Matrice risques fournisseurs"],
    livrablesVisuels: [
      {
        nom: "Registre Fournisseurs VRM",
        type: "Document",
        description: "Excel: 18 fournisseurs, 8 critiques, évaluations 3/8, scores risque, clauses contrats, roadmap 100%",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      },
      {
        nom: "Matrice Risques Fournisseurs",
        type: "Matrice",
        description: "Grille 2x2 criticité/risque: 8 fournisseurs positionnés, priorités évaluation, actions",
        imageUrl: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% actifs inventoriés: 78% (cible: 100%) | % fournisseurs critiques évalués: 40% (cible: 100%)"
  },
  {
    id: "P5-T03",
    nom: "Protéger",
    pilier: 5,
    description: "Développer et implémenter des protections pour assurer la délivrance des services critiques",
    descriptionDetaillee: "La fonction PROTECT du NIST CSF 2.0 est la première ligne de défense contre les cybermenaces. Elle comprend: 1) GESTION IDENTITÉ (PR.AA) - IAM implémenté, MFA activé, privilèges minimaux. 2) CONTRÔLE ACCÈS (PR.AC) - contrôle accès strict, révision régulière, principe moindre privilège. 3) SENSIBILISATION & FORMATION (PR.AT) - formation régulière, tests phishing, culture sécurité. 4) SÉCURITÉ DONNÉES (PR.DS) - chiffrement actif, sauvegardes sécurisées, classification données. 5) TECHNOLOGIES PROTECTRICES (PR.IP) - firewalls configurés, antivirus à jour, monitoring actif. 6) MAINTENANCE (PR.MA) - patches appliqués, maintenance préventive, inventaire à jour. Livrables: politique mots de passe/MFA, modules formation cyber, tests phishing simulés, rapports patch management, dashboard patch compliance, calendrier déploiement. Target: MFA 100%, formation 100%, patchs critiques <7j, conformité politique 95%+.",
    casUsage: "TechnoSolutions PROTECT 2024. MFA: 92% déployé (78/85 utilisateurs, 7 retardataires cible 100% T1 2025). Formation: 85% employés formés (nouveaux non formés onboarding), tests phishing 28% → 11% clics (cible <10%). Patch management: 94% systèmes patchés critiques <7j (cible 98%). Politique mdp: conformité 88% (12% mdp faibles legacy). Roadmap T1-T2 2025: MFA 100%, formation onboarding systématique, patchs 98%, conformité politique 95%. Bénéfice: première ligne défense renforcée vs vulnérabilités pré-NIST.",
    impactMetier: "MFA 92% → 100%, Formation 85% → 100%, Patchs critiques 94% <7j (cible 98%), Clics phishing 28% → 11% (cible <10%), Conformité politique 88% → 95%",
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: { responsible: "CyberOps BZ", accountable: "Directeur TI", consulted: "RH/Support", informed: "Tous employés" },
    livrables: ["Politique mots de passe/MFA documentée", "MFA déployé", "Modules formation cyber", "Tests phishing simulés", "Registre formations", "Rapports patch management", "Calendrier déploiement", "Dashboard patch compliance", "Dashboard conformité MFA"],
    livrablesVisuels: [
      {
        nom: "Politique Mots de Passe Formelle",
        type: "Document",
        description: "Document 6 pages: règles 12 caractères complexité, expiration 90j, gestionnaire 1Password, MFA obligatoire",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Conformité MFA",
        type: "Dashboard",
        description: "Métriques: 92% utilisateurs MFA (78/85), enrollment retardataires 7, conformité politique 88%, tendances",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      },
      {
        nom: "Résultats Tests Phishing Simulés",
        type: "Dashboard",
        description: "Évolution 12 mois: taux clics 28% → 11%, 1020 emails envoyés, signalements +500%, tendances",
        imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f72?w=800&auto=format&fit=crop"
      },
      {
        nom: "Modules Formation Cyber",
        type: "Outil",
        description: "Plateforme e-learning: formation initiale 45min, rappels trimestriels, micro-formations 5min post-clic",
        imageUrl: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard Patch Compliance",
        type: "Dashboard",
        description: "Temps réel: 94% patchés <7j, vulnérabilités critiques ouvertes 8, prochains patchs mardi, timeline",
        imageUrl: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Patch Management Mensuel",
        type: "Rapport",
        description: "Document 10 pages: patchs déployés 42, délais moyens 6.2j, compliance 94%, incidents 0, tendances",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% utilisateurs MFA: 92% (cible: 100%) | % employés formés: 85% (cible: 100%) | % systèmes patchés <7j: 94% (cible: 98%)"
  },
  {
    id: "P5-T04",
    nom: "Détecter",
    pilier: 5,
    description: "Développer et implémenter des activités pour identifier l'occurrence d'un événement de cybersécurité",
    descriptionDetaillee: "La fonction DETECT du NIST CSF 2.0 identifie proactivement les cybermenaces avant qu'elles ne causent des dommages. Elle comprend: 1) ANOMALIES & ÉVÉNEMENTS (DE.AE) - monitoring 24/7, alertes automatiques, corrélation événements. 2) MONITORING CONTINU SÉCURITÉ (DE.CM) - SIEM implémenté, logs centralisés, analyse comportementale. 3) PROCESSUS DÉTECTION (DE.DP) - procédures définies, escalade automatique, réponse rapide. Livrables: plateforme EDR opérationnelle, dashboard EDR temps réel, dashboard SIEM, rapports alertes, tests pénétration annuels, rapport pentest, plan remédiation pentest. Target: 100% endpoints couverts EDR, SIEM logs centralisés, temps détection <15min, 1 pentest/an.",
    casUsage: "TechnoSolutions DETECT 2024. EDR: Microsoft Defender ATP déployé 78% endpoints (67/85, 18 postes Win7 incompatibles remplacement T4). SIEM: Azure Sentinel configuré T3 ingestion logs AD, firewall, O365, EDR. Temps détection: 3h05 → 15min cible (amélioration -93%). Pentests: 0 tests actuel (gap majeur audit NIST). Roadmap T1 2025: EDR 100% (remplacement postes Win7), SIEM mature (règles corrélation avancées), pentest externe planifié 8.5k$ (infrastructure publique, VPN, web app). Bénéfice: détection rapide menaces vs aveuglement pré-EDR/SIEM (moyenne industrie 197 jours sans EDR/SIEM).",
    impactMetier: "Endpoints EDR 78% → 100%, Temps détection 3h05 → 15min (-93%), SIEM logs centralisés, Pentests 0 → 1/an, Visibilité menaces temps réel",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: { responsible: "CyberOps BZ", accountable: "Directeur TI", consulted: "Fournisseur EDR/Pentester", informed: "Support" },
    livrables: ["Plateforme EDR opérationnelle", "Dashboard EDR temps réel", "Dashboard SIEM Azure Sentinel", "Rapports alertes", "Tests pénétration annuels", "Rapport pentest externe", "Plan remédiation pentest"],
    livrablesVisuels: [
      {
        nom: "Dashboard EDR Temps Réel",
        type: "Dashboard",
        description: "Microsoft Defender ATP: 67 endpoints couverts 78%, menaces bloquées 23, alertes actives 2, détection 15min",
        imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop"
      },
      {
        nom: "Dashboard SIEM Azure Sentinel",
        type: "Dashboard",
        description: "Logs agrégés: AD, firewall, O365, EDR, alertes corrélation, incidents 8, investigation timeline",
        imageUrl: "https://images.unsplash.com/photo-1543286386-2e659306cd6c?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Pentest Externe",
        type: "Rapport",
        description: "Document 35 pages: vulnérabilités 12 identifiées CVSS 3.2-8.7, preuves exploitation, impact, remédiation",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      },
      {
        nom: "Plan Remédiation Pentest",
        type: "Document",
        description: "Roadmap: 12 vulnérabilités priorisées criticité, actions correctives, budgets 12k$, échéances 0-90j",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% endpoints couverts EDR: 78% (cible: 100%) | Temps détection: 15min | Pentests/an: 0 (cible: 1)"
  },
  {
    id: "P5-T05",
    nom: "Répondre",
    pilier: 5,
    description: "Développer et implémenter des activités pour agir face à un événement de cybersécurité détecté",
    descriptionDetaillee: "La fonction RESPOND du NIST CSF 2.0 organise la réponse aux incidents cyber pour limiter les dommages et éviter la récurrence. Elle comprend: 1) PLANIFICATION RÉPONSE (RS.RP) - plans documentés, rôles définis, procédures testées. 2) COMMUNICATIONS (RS.CO) - canaux établis, templates prêts, stakeholders informés. 3) ANALYSE (RS.AN) - analyse forensique, cause racine, impact évalué. 4) MITIGATION (RS.MI) - containment rapide, récupération efficace, leçons apprises. 5) AMÉLIORATIONS (RS.IM) - post-mortem, améliorations, prévention future. Livrables: registre incidents sécurité, rapports post-incident, plan correctif, procédures réponse incidents. Target: détection <4h, résolution P1 <12h, post-mortem systématique.",
    casUsage: "TechnoSolutions RESPOND 2024. Incidents: 12 détectés/résolus (8 phishing tentatives bloquées, 3 malware postes isolés, 1 ransomware contenu). Incident majeur août: tentative ransomware LockerGoga email phishing cliqué Marketing. Timeline: détection EDR 3h05 post-cryptage début, containment isolation réseau 10 min, investigation forensique 3h, eradication wipe/réimage 1h, recovery 1h, total résolution 5h25 vs target <12h. Post-mortem: formation phishing renforcée, filtrage email amélioré (règles SPF/DMARC strictes), MFA étendu 100% utilisateurs. Résolution moyenne 12 incidents: 2.8h (excellent vs industrie 23h moyenne). Bénéfice: réponse organisée efficace vs chaos pré-NIST.",
    impactMetier: "Incidents 12/an détectés/résolus, Résolution 2.8h moyenne (target <12h), Dommages minimisés, Leçons capitalisées, Post-mortem systématique",
    priorite: "Haute",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 5,
    raci: { responsible: "CyberOps BZ", accountable: "Directeur TI", consulted: "Experts externes", informed: "Direction" },
    livrables: ["Registre incidents sécurité", "Rapports post-incident", "Plan correctif", "Procédures réponse incidents"],
    livrablesVisuels: [
      {
        nom: "Registre Incidents Sécurité 2024",
        type: "Document",
        description: "Excel: 12 incidents, dates, types, vecteurs, dommages, délais résolution 2.8h, actions préventives",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Post-Incident Ransomware",
        type: "Rapport",
        description: "Forensique 15 pages: timeline, vecteur phishing, containment 10min, dommages 25 fichiers, leçons MFA",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Incidents détectés: 12/an | Temps résolution moyen: 2.8h (cible: <12h)"
  },
  {
    id: "P5-T06",
    nom: "Récupérer",
    pilier: 5,
    description: "Développer et implémenter des activités pour maintenir la résilience et restaurer les capacités",
    descriptionDetaillee: "La fonction RECOVER du NIST CSF 2.0 garantit la continuité d'affaires après un incident majeur. Elle comprend: 1) PLANIFICATION RÉCUPÉRATION (RC.RP) - plans de récupération, RTO/RPO définis, tests réguliers. 2) AMÉLIORATIONS (RC.IM) - leçons apprises, processus améliorés, formation mise à jour. 3) COMMUNICATIONS (RC.CO) - communication claire, stakeholders informés, confiance restaurée. Livrables: document PRA validé, tests simulation PRA, rapport post-exercice PRA, procédures récupération. Target: 100% services critiques couverts PRA, tests annuels obligatoires, RTO respecté.",
    casUsage: "TechnoSolutions RECOVER 2024. PRA élaboré T3: 5 services critiques identifiés (ERP, email, CRM, fichiers, réseau). Stratégies: ERP backup cloud 4x/jour RPO 6h (cible 1h amélioration future), migration Azure cloud géographiquement distribué, email Office 365 SLA 99.9% Microsoft. Test simulation décembre scenario ransomware crypte ERP. Résultats: activation PRA 45min, restauration ERP backup cloud 3h15 vs RTO 4h (respecté), communication employés SMS effectuée, gap identifié communication clients absente (ajoutée plan). Services couverts PRA: 75% (4/5 critiques, réseau manquant procédures complètes). Budget remédiation réseau 5k$ 2025. Target 100% T3 2025. Bénéfice: continuité garantie vs arrêt opérations pré-PRA.",
    impactMetier: "Services couverts PRA 75% → 100%, Tests annuels 1/an, RTO respecté 3h15 vs 4h cible, Gaps identifiés/corrigés, Continuité garantie",
    priorite: "Haute",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Fournisseurs", informed: "Tous employés" },
    livrables: ["Document PRA validé", "Tests simulation PRA", "Rapport post-exercice PRA", "Procédures récupération"],
    livrablesVisuels: [
      {
        nom: "Plan Reprise Activité (PRA)",
        type: "Document",
        description: "Manuel 52 pages: 5 services critiques RTO/RPO, stratégies backup/cloud, procédures, rôles, tests",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      },
      {
        nom: "Rapport Test Simulation PRA",
        type: "Rapport",
        description: "Résultats décembre 2024: scenario ransomware, activation 45min, récupération 3h15, gaps communication",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% services couverts PRA: 75% (cible: 100%) | Tests/an: 1 | RTO respecté: 3h15 vs 4h cible"
  },

  // PILIER 6 : Innovation (10 tactiques)
  {
    id: "P6-T01",
    nom: "Réserver budget pour innovation",
    pilier: 6,
    description: "Garantir ressources exploration nouvelles idées",
    descriptionDetaillee: "Budget innovation TI dédié garantit ressources explorer nouvelles technologies sans compromettre opérations courantes. Benchmark industrie: 8-10% budget TI total alloué innovation (vs 90-92% opérations/maintenance). Budget innovation finance: POC nouvelles solutions (cloud, IA, automation), formations technologies émergentes, consultants spécialisés, conférences/événements techno, R&D projets expérimentaux. Justification ROI: innovations génèrent économies long-terme (automatisations -85k$/an coût 12k$ initial = ROI 700% sur 3 ans), avantages compétitifs (nouveaux services clients), attraction talents (environnement techno moderne). Reporting trimestriel: utilisation budget innovation, ROI projets pilotes, leçons apprises. Target: 8-10% budget TI (vs 5% actuel 34k$).",
    casUsage: "TechnoSolutions budget TI 2024: 680k$ total dont 34k$ innovation (5% = sous-target). Utilisation: 12k$ automatisation rapports Power BI (ROI 85k$/an économies temps), 8k$ formation Azure cloud équipe, 6k$ POC IA chatbot support (rejeté faible adoption prévue), 5k$ consultant cybersécurité NIST, 3k$ événements techno (conférence Microsoft Ignite). Bénéfices tangibles: automatisation ROI 700%/3 ans, formation Azure migration cloud -28k$/an hébergement. Roadmap 2025: budget innovation relevé 8% (54k$) finançant POC IA assistée ticketing support, migration complète cloud, automatisations additionnelles RPA. Target 10% (68k$) 2026 stimulant innovation continue.",
    impactMetier: "Budget innovation 5% → 10%, ROI projets innovation 700%/3 ans, Automatisations économisant 85k$/an, Culture innovation renforcée",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 2,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "CFO", consulted: "Direction", informed: "Équipe TI" },
    livrables: ["Budget innovation distinct", "Rapport utilisation", "ROI innovation"],
    livrablesVisuels: [
      {
        nom: "Rapport Utilisation Budget Innovation",
        type: "Rapport",
        description: "Document: 34k$ dépensé 5 initiatives, ROI 700% automation, leçons POC IA, roadmap 2025 54k$",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% budget TI innovation: 5% (34k$) | Cible: 8-10%"
  },
  {
    id: "P6-T02",
    nom: "Encourager adoption nouvelles technologies",
    pilier: 6,
    description: "Rester compétitif et moderniser processus",
    descriptionDetaillee: "Adoption nouvelles technologies maintient compétitivité, améliore productivité, attire/retient talents. Processus: 1) IDENTIFICATION - veille techno identifie opportunités (Cloud Azure, Microsoft 365 nouvelles fonctions, Power Platform automation, IA assistée). 2) ÉVALUATION - business case ROI/effort, POC pilote. 3) PLANIFICATION - roadmap déploiement phases, formation utilisateurs, communication changement. 4) DÉPLOIEMENT - rollout progressif 20% utilisateurs early adopters → 100%, support renforcé période transition. 5) MESURE - taux adoption %,délai adoption cible <6 mois, satisfaction utilisateurs. Facteurs succès: sponsorship direction visible, formations adéquates, quick wins communiqués. Target: adoption 80%+ <4 mois.",
    casUsage: "TechnoSolutions adoption Microsoft Teams 2024 (collaboration/chat remplaçant emails internes). Plan: T1 POC 15 utilisateurs département Marketing (succès NPS 8.5/10), T2 déploiement 50% entreprise formation 2h sessions, T3-T4 rollout 100%. Résultats 8 mois: adoption 68% utilisateurs actifs quotidiens (cible 80% manquée), délai adoption 4 mois (cible atteinte). Freins adoption: employés terrain peu bureaux (25%) préfèrent téléphone, seniors 55+ résistance changement (42% adoption vs 85% <40 ans). Actions amélioration: formation one-on-one seniors, ambassadeurs Teams départements, gamification adoption (concours équipe collaboration mensuelle). Target 80% T2 2025.",
    impactMetier: "Adoption Teams 68% → 80%, Délai adoption 4 mois (target), Emails internes -45%, Collaboration améliorée, Productivité +12%",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Utilisateurs", informed: "Tous employés" },
    livrables: ["Plan déploiement techno", "Sessions formation", "Documentation"],
    livrablesVisuels: [
      {
        nom: "Rapport Adoption Teams",
        type: "Rapport",
        description: "Document: adoption 68%, délai 4 mois, freins identifiés seniors/terrain, actions amélioration, roadmap 80%",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Taux adoption: 68% (cible: 80%+) | Délai adoption: 4 mois"
  },
  {
    id: "P6-T03",
    nom: "Automatiser processus manuels",
    pilier: 6,
    description: "Réduire erreurs et libérer temps haute valeur",
    descriptionDetaillee: "Automation processus manuels répétitifs libère temps humain tâches haute valeur créative/stratégique. Identification candidats automation: processus répétitifs fréquents (quotidiens/hebdomadaires), règles structurées (si/alors logique claire), volumes élevés (>50 transactions/mois), prone erreurs humaines. Technologies: RPA (Robotic Process Automation = UiPath, Power Automate), scripts Python/PowerShell, workflows tools (Zapier, Make), macros Excel/VBA. ROI automation: temps économisé valorisé coût horaire ($50/h moyenne), réduction erreurs (coût corrections), scalabilité (capacité traiter volumes accrus sans headcount). Target: identifier/automatiser 15-20 processus manuels/an, économies 80-100k$/an.",
    casUsage: "TechnoSolutions automatisations 2024: 12 processus automatisés. Exemples: 1) Génération rapports mensuels TI Power BI (temps manuel 10h → automatique 15 min = économie 117h/an), 2) Onboarding employés AD/O365 script PowerShell (45 min → 5 min = 40 min x 24 nouveaux/an = 16h/an), 3) Backup vérifications automatiques emails alertes (2h/sem → automatique = 104h/an), 4) Tickets récurrents bot auto-résolution (mots passe, VPN configs = 156 tickets/an x 15 min = 39h/an). Total économies temps: 276h/an valorisé $50/h = $13,800/an + amélioration qualité réduction erreurs 85%. Coûts automation: 12k$ développement initial + 2k$/an maintenance = ROI 115% année 1, 690% sur 3 ans. Target 2025: 8 automatisations additionnelles (108h économisées) cumulant 85k$/an économies.",
    impactMetier: "Processus automatisés 12 → 20, Temps économisé 276h → 384h/an, Économies 13.8k$ → 85k$/an, Erreurs -85%, ROI 690%/3 ans",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 4,
    raci: { responsible: "Équipe prog BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Rapport processus automatisés", "Scripts/outils", "Documentation flux"],
    livrablesVisuels: [
      {
        nom: "Rapport Automatisations 2024",
        type: "Rapport",
        description: "Document: 12 processus automatisés, économies 276h/an ($13.8k), ROI 690%/3 ans, roadmap 2025",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      },
      {
        nom: "Bibliothèque Scripts Automation",
        type: "Outil",
        description: "Repository: 12 scripts PowerShell/Python, workflows Power Automate, documentation, maintenance",
        imageUrl: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Processus automatisés: 12 | Réduction temps: 35% | Économies: 85k$/an"
  },
  {
    id: "P6-T04",
    nom: "Mesurer réduction coût TI par utilisateur",
    pilier: 6,
    description: "Démontrer efficacité et optimisation",
    descriptionDetaillee: "Coût TI par utilisateur (TCO = Total Cost Ownership) mesure efficacité départe ment TI. Calcul: (Budget TI total annuel / Nombre employés) = coût/user. Benchmark PME services: 8,000-12,000$/user (médiane 9,500$). Optimisations réduisant coût: migration cloud (vs serveurs locaux CAPEX élevé), automatisations (moins FTE support nécessaires), négociations fournisseurs (licenses bulk), solutions SaaS (vs développements custom coûteux). Analyse annuelle décompose coûts: infrastructure 35%, applications 25%, personnel TI 30%, support/formation 10%. Target: réduction 3-5%/an via efficacité opérationnelle maintenant qualité services.",
    casUsage: "TechnoSolutions coût TI 2024: budget 680k$, 75 employés = 9,067$/user (vs benchmark 9,500$ = -4.6% meilleur). Décomposition: infrastructure 238k$ (35%), applications 170k$ (25%), personnel 204k$ (30%), support/formation 68k$ (10%). Optimisations 2024: migration Azure cloud -28k$/an vs serveurs locaux, automatisations Power BI -13.8k$/an, négociation licenses Microsoft -8k$/an = économies totales 49.8k$/an (-7.3% budget). Coût/user projeté 2025: (680k-50k) / 80 employés = 7,875$/user = réduction 13% maintenant qualité services amélioration NPS 8.3/10. Roadmap: target maintien <9,000$/user croissance entreprise scaling efficace TI.",
    impactMetier: "Coût TI/user 9,067$ → 7,875$ (-13%), Optimisations 49.8k$/an, Benchmark -17% vs industrie, Qualité maintenue NPS 8.3/10",
    priorite: "Moyenne",
    statut: "Fait",
    asWas: 2,
    asIs: 4,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "CFO", consulted: "Finance", informed: "Direction" },
    livrables: ["Rapports annuels coût/user", "Benchmarks comparatifs", "Plan optimisation"],
    livrablesVisuels: [
      {
        nom: "Rapport Coût TI/User 2024",
        type: "Rapport",
        description: "Document: 9,067$/user, décomposition coûts, benchmark -4.6%, optimisations 49.8k$, roadmap 2025",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Coût TI/user: 9,067$ (cible: <9,000$) | Réduction annuelle: 5%"
  },
  {
    id: "P6-T05",
    nom: "Ateliers innovation avec équipe",
    pilier: 6,
    description: "Stimuler créativité collective",
    descriptionDetaillee: "Ateliers innovation = sessions brainstorming structurées équipe TI (+ invités autres départements) générées idées améliorations/nouvelles solutions. Format: half-day 3-4h, facilitateur externe neutre, techniques créativité (design thinking, mind mapping, SCAMPER, reverse brainstorming). Thèmes: automatisations opportunités, expérience utilisateur améliorations, sécurité renforcée, outils collaboration nouveaux. Output: 15-25 idées générées, voting équipe priorités top 5, sponsors assignés idées prometteuses, plan action 90j POC/implémentation. Fréquence: 2 ateliers/an stimulant culture innovation. Bénéfices: engagement équipe accru, ownership idées bottom-up, innovation démocratisée (pas juste direction).",
    casUsage: "TechnoSolutions ateliers innovation planifiés 2025 (actuel 0 = gap culture innovation). Plan: T2 atelier #1 thème automation opportunités (invités départements Finance, RH, Ventes identifiant processus manuels), T4 atelier #2 thème expérience utilisateur TI (interface portail services, catalogues, formations). Budget: 2 x 2k$ facilitateur externe + 1 jour équipe TI (coût opportunity 3k$) = 7k$ total. Résultats attendus (basé benchmarks): 20 idées/atelier, 5 priorisées top, 2-3 implémentées (10-15% taux conversion idées → réalisations = normal). ROI estimé: automatisations additionnelles 50k$/an économies (investissement 7k$ = ROI 700%). Culture innovation renforcée engagement équipe.",
    impactMetier: "Ateliers 0 → 2/an, Idées générées 40, Implémentations 4-6, ROI estimé 700%, Engagement équipe accru, Culture innovation",
    priorite: "Basse",
    statut: "Optionnel",
    asWas: 0,
    asIs: 1,
    target: 3,
    raci: { responsible: "Directeur TI BZ", accountable: "RH", consulted: "Équipe TI", informed: "Direction" },
    livrables: ["CR ateliers", "Idées générées", "Plan suivi"],
    livrablesVisuels: [
      {
        nom: "Rapport Atelier Innovation T2",
        type: "Rapport",
        description: "Document: 22 idées automation, top 5 priorisées, sponsors assignés, plan 90j, photos session",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Ateliers/an: 0 (cible: 2) | Idées mises en œuvre: TBD"
  },
  {
    id: "P6-T06",
    nom: "Suivre tendances technologiques (veille)",
    pilier: 6,
    description: "Anticiper changements et identifier opportunités",
    descriptionDetaillee: "Veille technologique proactive = surveillance continue tendances émergentes évitant obsolescence et identifiant opportunités compétitives. Sources: publications techno (Gartner, Forrester reports), conférences (Microsoft Ignite, AWS re:Invent, webinaires), podcasts/newsletters (The Vergecast, TechCrunch), réseaux sociaux LinkedIn thought leaders, forums communautés (Reddit r/sysadmin, Stack Overflow). Processus: lecture hebdo 2-3h Directeur TI, curation insights pertinents PME services (filtrage hype vs applicable), rapport trimestriel 10 pages: 5-8 tendances analysées (ex: IA générative, zero-trust sécurité, low-code platforms), applicabilité TechnoSolutions, recommandations actions. Target: 4 rapports/an, 60% recommandations adoptées roadmap.",
    casUsage: "TechnoSolutions veille techno 2024: 4 rapports trimestriels produits. Tendances identifiées: 1) IA générative Microsoft Copilot Office 365 (recommandation POC adoption T1 2025 = adoptée roadmap), 2) Zero-trust architecture cybersécurité (recommandation audit NIST segmentation réseau = adoptée budget 2025 8k$), 3) Low-code Power Platform automation (recommandation formations équipe = adoptée 8k$ formations), 4) Infrastructure as Code Terraform (recommandation POC migration cloud = reportée 2026 complexité). 5) Edge computing (non-applicable PME services = rejetée). Taux adoption recommandations: 60% (3/5 adoptées roadmap/budget). Bénéfices: anticipation tendances vs réaction tardive concurrence, décisions technologiques informées data vs intuition.",
    impactMetier: "Rapports veille 4/an, Tendances 20 identifiées, Recommandations adoptées 60%, Décisions informées, Anticipation vs réaction",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Experts", informed: "Équipe TI" },
    livrables: ["Rapport veille trimestriel", "Comparatif tendances", "Recommandations"],
    livrablesVisuels: [
      {
        nom: "Rapport Veille Techno T4 2024",
        type: "Rapport",
        description: "Document 12 pages: 5 tendances analysées IA/zero-trust/low-code, applicabilité, recommandations 3 adoptées",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Rapports produits: 4/an | % recommandations adoptées: 60%"
  },
  {
    id: "P6-T07",
    nom: "Expérimenter POC chaque année",
    pilier: 6,
    description: "Tester rapidement avant déploiement large",
    descriptionDetaillee: "POC (Proof of Concept) = pilote rapide limité testant faisabilité/viabilité nouvelle technologie AVANT investissement déploiement complet. Processus: 1) SÉLECTION - identification technologie prometteuse (IA chatbot support, RPA automation, zero-trust réseau). 2) PLANNING - périmètre limité (10-15 utilisateurs, 4-8 semaines), budget 5-15k$, KPI succès définis. 3) EXÉCUTION - déploiement pilote, monitoring usage, collecte feedbacks. 4) ÉVALUATION - analyse ROI, satisfaction utilisateurs, faisabilité technique, coûts déploiement large. 5) DÉCISION - GO (intégration roadmap projets), NO-GO (abandon technologie), PIVOT (ajustements périmètre). Target: 3-4 POC/an, 50% convertis projets.",
    casUsage: "TechnoSolutions POC 2024: 2 réalisés (cible 3-4 manquée ressources limitées). POC #1: IA chatbot support ticketing automation (budget 6k$, 6 semaines, 15 utilisateurs pilote). Résultats: résolution 28% tickets niveau 1 (FAQ, reset mot passe), satisfaction 6.8/10 (acceptable), coût déploiement large 25k$ + 8k$/an maintenance. Décision: NO-GO ROI insuffisant vs bénéfices (28% automatisation faible, coût élevé). POC #2: Power Platform RPA automatisation rapports Finance (budget 8k$, 8 semaines). Résultats: automatisation 3 rapports mensuels économisant 15h/mois (180h/an = $9k$/an), satisfaction 9.2/10, déploiement large 12k$. Décision: GO intégré roadmap T1 2025. Conversion: 50% (1/2 POC → projet). Leçons: POC éliminent risques investissements tech inadéquats, validation rapide hypothèses.",
    impactMetier: "POC 2/an réalisés (cible 3-4), Conversion projets 50%, Risques tech éliminés, Décisions informées data vs hypothèses",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 2,
    target: 4,
    raci: { responsible: "Équipe prog BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Rapport POC", "Évaluation faisabilité", "Décision go/no-go"],
    livrablesVisuels: [
      {
        nom: "Rapport POC Chatbot IA",
        type: "Rapport",
        description: "Document: résolution 28% tickets, satisfaction 6.8/10, coût 25k$, décision NO-GO ROI faible",
        imageUrl: "https://images.unsplash.com/photo-1590935216541-5d5c0632ee13?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "POC réalisés: 2/an (cible: 3-4) | % convertis projets: 50%"
  },
  {
    id: "P6-T08",
    nom: "Innovation Funnel (idée → déploiement)",
    pilier: 6,
    description: "Structurer processus innovation en étapes",
    descriptionDetaillee: "Innovation funnel structure flux idées → réalisations 4 étapes: 1) IDEATION (collecte idées large employés/veille/ateliers, 40-60 idées/an), 2) FILTRAGE (évaluation rapide faisabilité/impact, sélection 15-20 prometteuses), 3) DÉVELOPPEMENT (POC/MVP 3-4 initiatives, budget innovation dédié), 4) DÉPLOIEMENT (2-3 projets validés intégration roadmap TI). Métriques: taux conversion 5-10% idées → déploiement (normal processus sélectif rigoureux), délai cycle moyen 6-12 mois ideation → production. Registre initiatives PRIsME track: statut idée (ideation/filtered/POC/deployed/rejected), sponsors, budgets, KPI. Bénéfice: structuration innovation chaos → processus managé.",
    casUsage: "TechnoSolutions innovation funnel établi 2025 (actuel 0 = gap processus structuré). Plan: T1 lancement appel idées all-hands (email, intranet, ateliers innovation) attendu 45 idées. T2 comité innovation (Directeur TI, 2 tech seniors, sponsor RH) évalue filtrage top 18 faisables/impactantes. T3-T4 développement 4 POC budget innovation 34k$ (chatbot IA, RPA Finance, BI avancée, zero-trust réseau). 2026 déploiement 2 projets validés (estimation 50% conversion POC). Métriques attendues: taux conversion 4.4% (2/45 idées → projets = conforme benchmark 5-10%), délai cycle 12 mois. Funnel élimine innovation ad-hoc favorisant approche systématique.",
    impactMetier: "Funnel structuré établi, Idées 45 collectées, Conversion 4.4% (2 projets), Délai cycle 12 mois, Innovation managée vs chaos",
    priorite: "Basse",
    statut: "À faire",
    asWas: 0,
    asIs: 1,
    target: 3,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Équipe innovation", informed: "Tous" },
    livrables: ["Schéma funnel", "Registre initiatives", "Tableau suivi"],
    livrablesVisuels: [
      {
        nom: "Schéma Innovation Funnel",
        type: "Processus",
        description: "Diagramme: 4 étapes ideation→filtrage→POC→déploiement, 45→18→4→2 idées, délai 12 mois, KPI",
        imageUrl: "https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% idées entrant funnel: TBD | Délai cycle: TBD"
  },
  {
    id: "P6-T09",
    nom: "Concours interne innovation TI",
    pilier: 6,
    description: "Stimuler créativité et implication employés",
    descriptionDetaillee: "Concours innovation annuel = compétition interne stimulant créativité tous employés (pas juste TI). Format: appel projets 3 mois, soumissions individus/équipes (max 5 personnes), catégories (automatisation, expérience utilisateur, sécurité, durabilité), jury évaluation (Directeur TI, direction, sponsor externe), critères (innovation, faisabilité, impact, ROI). Prix: 1er place 3k$ budget développement projet + recognition all-hands, 2e 1.5k$, 3e 500$. Participation attendue: 10-15% employés (8-12 projets soumis entreprise 85 personnes). Bénéfices: engagement employés accru, ownership innovation démocratisée, idées bottom-up valorisées, culture innovation renforcée.",
    casUsage: "TechnoSolutions concours innovation planifié 2025 première fois (actuel 0 = gap culture innovation). Plan: lancement T2 appel projets 3 mois deadline T4, budget total 5k$ prix + 2k$ organisation événement = 7k$. Projets attendus: 10 soumissions (12% participation). Gagnants potentiels: automatisation processus RH onboarding (1er place 3k$ développement), dashboard BI temps réel ventes (2e), portail self-service employés amélioré (3e). ROI estimé: projet gagnant automatisation économies 25k$/an vs investissement 10k$ (concours 7k$ + développement 3k$) = ROI 250% première année. Culture innovation renforcée reconnaissance innovation tous niveaux hiérarchiques.",
    impactMetier: "Concours 0 → 1/an, Participation 10 projets (12%), ROI gagnant 250%, Culture innovation renforcée, Engagement employés accru",
    priorite: "Basse",
    statut: "Optionnel",
    asWas: 0,
    asIs: 0,
    target: 3,
    raci: { responsible: "RH", accountable: "Direction", consulted: "Équipe TI", informed: "Tous employés" },
    livrables: ["Annonce concours", "Projets soumis", "Suivi gagnants"],
    livrablesVisuels: [
      {
        nom: "Règlement Concours Innovation",
        type: "Document",
        description: "Document 8 pages: catégories, critères évaluation, prix 3k$/1.5k$/500$, timeline, jury, FAQ",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Concours/an: 0 (cible: 1) | Participation: TBD"
  },
  {
    id: "P6-T10",
    nom: "Suivre % temps innovation par équipe",
    pilier: 6,
    description: "Garantir innovation reste priorité",
    descriptionDetaillee: "Suivi temps innovation = allocation 10-15% temps équipe TI projets innovation/expérimentation (vs 85-90% opérations/maintenance). Modèle Google '20% time' inspirant: employés consacrent 1 jour/semaine (20%) projets personnels/innovation générant produits Gmail, AdSense. PME adaptation réaliste: 10-15% temps = 0.5-1 jour/semaine ou sprints innovation dédiés (1 semaine/trimestre focus 100% innovation). Activités innovation: POC nouvelles technologies, automatisations opportunités, amélioration outils internes, formations technologies émergentes, veille techno, prototypes. Tracking: timesheets catégorie 'Innovation', rapports mensuels % temps innovation/équipe, benchmark industrie 10-15%. Target: atteindre maintenir 10-15% temps innovation.",
    casUsage: "TechnoSolutions temps innovation 2024: <5% estimé (pas tracking formel = gap visibilité). Activités innovation ad-hoc: POC chatbot IA 3 semaines Tech senior (12% temps trimestre), automatisations Power BI 2 semaines Tech junior (8%), formations Azure 5 jours année (2%). Moyenne estimée: 3-5% temps total innovation vs cible 10-15%. Plan 2025: formalisation tracking temps innovation timesheets catégorie dédiée, sprints innovation trimestriels 1 semaine focus 100% (4 semaines/an = 7.7% temps), communications direction importance innovation 10-15% temps acceptable business. Target 2025: 12% temps innovation mesuré formellement, 2026 maintien 12-15%. Bénéfice: légitimation temps innovation vs culpabilité 'perte temps opérations'.",
    impactMetier: "Temps innovation <5% → 12%, Tracking formel établi, Sprints innovation trimestriels, Innovation légitimée vs culpabilité",
    priorite: "Basse",
    statut: "À faire",
    asWas: 1,
    asIs: 1,
    target: 3,
    raci: { responsible: "Directeur TI BZ", accountable: "RH", consulted: "Équipe TI", informed: "Direction" },
    livrables: ["Rapports suivi temps", "Indicateurs par équipe", "Comparatif"],
    livrablesVisuels: [
      {
        nom: "Rapport Temps Innovation Mensuel",
        type: "Rapport",
        description: "Dashboard: équipe TI temps innovation 12%, par personne tech senior 18%/junior 8%, activités détails",
        imageUrl: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% temps innovation: <5% (cible: 10-15%)"
  },

  // PILIER 7 : IA & Programmation (9 tactiques abrégées)
  {
    id: "P7-T01",
    nom: "Définir stratégie AI alignée objectifs entreprise",
    pilier: 7,
    description: "Vision cohérente utilisation IA créant valeur",
    descriptionDetaillee: "Stratégie IA n'est PAS 'utiliser IA partout' mais répondre : quels problèmes métiers IA peut résoudre mieux/plus vite/moins cher? Document stratégique (15-20 pages) couvre : vision IA (où dans 3 ans?), 8-10 cas usage priorisés (valeur/faisabilité), architecture technologique (cloud, on-premise, hybrid), gouvernance (comité IA, charte éthique), budget (% TI dédié IA), compétences (formations, recrutements), roadmap 18 mois. Validation direction formelle transforme IA d'expérimentations dispersées en programme structuré ROI mesurable. Révision semestrielle ajuste stratégie selon évolutions technologiques rapides.",
    casUsage: "TechnoSolutions définit stratégie IA focus 3 cas usage haute valeur: 1) Chatbot support réduisant tickets -40% (ROI: +120k$/an), 2) Analyse prédictive maintenance équipements évitant pannes (ROI: +85k$/an), 3) Automatisation génération rapports clients économisant 15h/semaine (ROI: +65k$/an). Total ROI projeté: +270k$/an pour investissement 180k$.",
    impactMetier: "ROI +185%, Productivité +45%, Avantage compétitif significatif, Innovation accélérée",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Experts IA", informed: "Tous" },
    livrables: ["Document stratégie AI", "Feuille route pluriannuelle", "Validation direction"],
    livrablesVisuels: [
      {
        nom: "Document Stratégie IA Complète",
        type: "Document",
        description: "Document 20 pages : vision, cas usage, architecture, gouvernance, budget, roadmap validé direction",
        imageUrl: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&auto=format&fit=crop"
      },
      {
        nom: "Matrice Cas Usage IA",
        type: "Matrice",
        description: "Tableau 2x2 Valeur/Faisabilité positionnant 15 cas usage identifiés, priorisation top 8",
        imageUrl: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&auto=format&fit=crop"
      },
      {
        nom: "Roadmap IA 18 mois",
        type: "Présentation",
        description: "Timeline visuelle : POC planifiés, déploiements, formations, jalons clés, budget trimestriel",
        imageUrl: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Cas usage stratégiques: 8 identifiés | % initiatives AI alignées: 88%"
  },
  {
    id: "P7-T02",
    nom: "Identifier et tester cas usage AI",
    pilier: 7,
    description: "Passer théorie à pratique avec cas concrets",
    descriptionDetaillee: "Identification cas usage IA transforme stratégie abstraite en applications concrètes valeur mesurable. Processus: 1) WORKSHOP directions métiers brainstorm douleurs/opportunités (40-60 idées). 2) FILTRAGE matrice valeur/faisabilité (IA adéquate?, données disponibles?, ROI >100%?) sélection 8-12 cas. 3) PRIORISATION scoring valeur business, complexité technique, délais implémentation. 4) TEST POC top 3-5 cas usage 8-12 semaines validation hypothèses. 5) DÉCISION GO (roadmap projets), NO-GO (abandon), PIVOT (ajustement). Exemples cas usage PME: chatbot support, prédiction maintenance, automatisation rapports, analyse sentiments clients, détection fraudes. Target: 8 cas identifiés, 5 testés POC, 80% réussite → projets.",
    casUsage: "TechnoSolutions 2024 workshops identifient 8 cas usage IA: chatbot support, automatisation rapports clients, prédiction inventaire, analyse feedback clients NLP, détection anomalies sécurité, optimisation planning techniciens, scoring leads ventes, transcription automatique réunions. Priorisation: top 5 testés POC (chatbot, rapports, inventaire, feedback, anomalies). Résultats: 4/5 POC succès (80% = target atteint) = chatbot NO-GO adoption faible, 4 autres GO roadmap 2025. Bénéfice: validation terrain vs assumptions, ROI prouvé avant investissements lourds.",
    impactMetier: "Cas usage 8 identifiés, POC 5 testés, Réussites 80% (4/5), Projets roadmap 4, ROI validé avant investissements",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 5,
    raci: { responsible: "Équipe prog BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Liste cas usage", "Rapports test", "POC"],
    livrablesVisuels: [
      {
        nom: "Matrice Cas Usage IA",
        type: "Matrice",
        description: "Grille valeur/faisabilité: 8 cas usage positionnés, top 5 priorisés POC, décisions GO/NO-GO",
        imageUrl: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Cas usage identifiés: 8 | Testés: 5 | % réussites: 80%"
  },
  {
    id: "P7-T03",
    nom: "Cadre éthique et légal usage AI",
    pilier: 7,
    description: "Éviter dérives (biais, conformité, protection données)",
    descriptionDetaillee: "Cadre éthique IA établit garde-fous prévenant dérives (biais algorithmes discriminatoires, violations vie privée, non-conformités RGPD/Loi 25 Québec). Charte utilisation IA 10-15 pages documente: principes éthiques (transparence, équité, responsabilité, vie privée), interdictions (décisions automatisées discriminatoires emploi/crédit, surveillance employés invasive, manipulation opinions), gouvernance (comité éthique IA valide projets), conformité légale (RGPD/Loi 25 consentement données, droits rectification/oubli). Registre conformité track projets IA: cas usage, données utilisées, consentements obtenus, analyses impact vie privée (AIPVP). Audits éthiques annuels tiers indépendants. Target: 100% projets IA conformes charte.",
    casUsage: "TechnoSolutions charte éthique IA élaborée T4 2024 consultante spécialisée (coût 8k$). Principes: transparence explications décisions IA, équité tests biais, vie privée anonymisation données. Interdictions: profilage employés surveillance, décisions automatisées RH sans humain boucle. Registre: 5 projets IA trackés (chatbot, rapports, inventaire, feedback, anomalies). Conformité: 4/5 conformes (80% vs cible 100%), 1 non-conformité = projet analyse feedback clients utilise données sans consentement explicite (correction Loi 25 consentement ajouté). Audit éthique prévu T2 2025 validation externe. Bénéfice: confiance utilisateurs/clients, réduction risques légaux.",
    impactMetier: "Charte éthique établie, Projets conformes 75% → 100%, Non-conformités corrigées, Risques légaux réduits, Confiance accrue",
    priorite: "Haute",
    statut: "En cours",
    asWas: 0,
    asIs: 2,
    target: 5,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Avocat", informed: "Tous employés" },
    livrables: ["Charte utilisation AI", "Registre conformité", "Audit éthique"],
    livrablesVisuels: [
      {
        nom: "Charte Éthique IA",
        type: "Document",
        description: "Document 14 pages: principes transparence/équité/vie privée, interdictions, gouvernance, conformité RGPD/Loi 25",
        imageUrl: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets AI conformes: 75% (cible: 100%) | Non-conformités: 2"
  },
  {
    id: "P7-T04",
    nom: "Expérimenter POC AI et mesurer valeur",
    pilier: 7,
    description: "Évaluer efficacité avant déploiement complet",
    descriptionDetaillee: "POC IA = pilots rapidement limités (8-12 semaines, 10-20 utilisateurs, budget 10-25k$) testent hypothèses valeur AVANT investissements déploiements complets (100-300k$+). Méthodologie: 1) HYPOTHÈSES - définir métriques succès quantifiées (ex: chatbot résout 40%+ tickets niveau 1, NPS >7/10, coûts <15k$). 2) MVP - développer version minimale viable fonctionnelle testable. 3) TEST - déploiement pilote utilisateurs représentatifs, monitoring usage, collecte feedbacks. 4) MESURE - analyse ROI (coûts vs bénéfices mesurés), satisfaction utilisateurs, faisabilité technique scaling. 5) DÉCISION - GO roadmap si ROI >100% satisfaction >7/10, NO-GO sinon. Target: 3 POC IA/an, 67% conversion projets, ROI moyen 185%.",
    casUsage: "TechnoSolutions POC IA 2024: 3 réalisés. POC #1 chatbot support (budget 15k$, 10 semaines): résolution 28% tickets (vs hypothèse 40%), NPS 6.8/10 (vs 7+), coûts déploiement complet 65k$ → NO-GO ROI faible. POC #2 automatisation rapports clients NLP (budget 12k$, 8 semaines): génération 85% rapports automatique vs 10% manuel, NPS 8.9/10, économies 180h/an ($9k/an), déploiement 18k$ → GO roadmap T1 2025 ROI 100% an 2. POC #3 prédiction inventaire ML (budget 18k$, 12 semaines): réduction ruptures stock 35%, optimisation capital 22k$/an, déploiement 28k$ → GO roadmap T2 2025 ROI 157% an 3. Conversion: 67% (2/3 → projets). ROI moyen projets GO: (100%+157%)/2 = 128.5% (vs target 185%).",
    impactMetier: "POC 3/an, Conversion 67%, ROI moyen 128% (target 185%), Décisions GO/NO-GO informées data, Risques minimisés",
    priorite: "Haute",
    statut: "En cours",
    asWas: 0,
    asIs: 3,
    target: 4,
    raci: { responsible: "Équipe prog BZ", accountable: "Directeur TI", consulted: "Utilisateurs", informed: "Direction" },
    livrables: ["Rapports POC", "Analyse coûts/bénéfices", "Décision go/no-go"],
    livrablesVisuels: [
      {
        nom: "Rapport POC Automatisation Rapports",
        type: "Rapport",
        description: "Document: automatisation 85%, NPS 8.9/10, économies 9k$/an, ROI 100%, décision GO",
        imageUrl: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "POC AI/an: 3 | % convertis projets: 67% | ROI moyen: 185%"
  },
  {
    id: "P7-T05",
    nom: "Former équipes usages et limites AI",
    pilier: 7,
    description: "Adoption réussie nécessite compréhension potentiel ET limites",
    descriptionDetaillee: "Formation IA démystifie technologie, évite attentes irréalistes ('IA résout tout magiquement'), et maximise adoption réussie. Programme: 1) SENSIBILISATION générale all-hands 1h (qu'est IA?, applications concrètes, limitations, éthique) = 100% employés. 2) FORMATION utilisateurs 3h hands-on outils IA déployés (chatbot, rapports automatisés, outils analyse) = départements concernés. 3) FORMATION développeurs 2j techniques IA (ML Azure, API OpenAI, prompting engineering, fine-tuning) = équipe prog. 4) E-LEARNING modules optionnels approfondissement (Computer Vision, NLP, reinforcement learning). 5) QUIZ évaluation compréhension post-formation. Target: 90% équipes formées, score compréhension 80%+.",
    casUsage: "TechnoSolutions formations IA 2024: sensibilisation 85 employés (100%), formations utilisateurs rapports IA 45 employés concernés (53% vs cible 90%), formation développeurs IA 3 devs (100% équipe prog). E-learning: 12 employés optent modules approfondissement. Quiz post: score moyen compréhension 72% (vs cible 80%), gaps identifiés concepts biais/éthique. Actions amélioration T1 2025: sessions rattrapage 40 employés manquants formations utilisateurs, modules quiz renforcés éthique. Budget formations 2024: 15k$ (formateur externe, licenses e-learning, temps employés). Bénéfice: adoption réussie outils IA déployés NPS 8.2/10 vs 6.5/10 employés non-formés.",
    impactMetier: "Employés formés 55% → 90%, Compréhension 72% → 80%, Adoption outils IA NPS 8.2/10, Attentes réalistes vs irréalistes",
    priorite: "Haute",
    statut: "En cours",
    asWas: 1,
    asIs: 2,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "RH", consulted: "Formateurs", informed: "Tous employés" },
    livrables: ["Plan formation", "Modules e-learning", "Registres participation"],
    livrablesVisuels: [
      {
        nom: "Rapport Formations IA 2024",
        type: "Rapport",
        description: "Document: 85 employés sensibilisés, 53% formés outils, score 72%, actions amélioration, budget 15k$",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% équipes formées: 55% (cible: 90%) | Score compréhension: 72%"
  },
  {
    id: "P7-T06",
    nom: "Veille stratégique évolutions AI",
    pilier: 7,
    description: "Domaine évoluant vite - anticiper ruptures technologiques",
    descriptionDetaillee: "Veille IA = surveillance continue innovations IA évoluant rapidité exponentielle (GPT-3 → GPT-4 12 mois, nouveaux modèles LLM mensuels). Sources: conférences IA (NeurIPS, ICML), publications recherche (arXiv.org), newsletters spécialisées (ImportAI, The Batch Andrew Ng), blogs leaders (OpenAI, Google AI, Anthropic), réseaux sociaux experts IA LinkedIn/Twitter. Processus: lecture hebdo 2-3h Directeur TI, curation insights applicables PME (filtrage recherche académique vs solutions business-ready), rapport trimestriel 8-12 pages: 5-8 évolutions majeures (ex: GPT-4 Turbo, Claude 3, Gemini Ultra, agents autonomes, multimodalité), applicabilité TechnoSolutions, recommandations intégration stratégie IA. Target: 4 rapports/an, 50% recommandations intégrées.",
    casUsage: "TechnoSolutions veille IA 2024: 4 rapports trimestriels produits. Évolutions identifiées: 1) GPT-4 Turbo coûts réduits -60% (recommandation migration chatbot POC GPT-3.5 → GPT-4 Turbo = adoptée budget 2025). 2) Azure AI Studio workflows simplifiés (recommandation formations équipe = adoptée). 3) Agents autonomes LangChain orchestration (recommandation POC agents automatisation workflows complexes = reportée 2026 maturité insuffisante). 4) Multimodalité GPT-4 Vision analyse documents/images (recommandation POC OCR factures automatisation comptabilité = évaluée T1 2025). 5) Open-source alternatives (Mistral, Llama 3) = non-applicable PME préférence cloud managé. Taux intégration: 50% (2/4 adoptées roadmap). Bénéfice: stratégie IA actualisée vs obsolescence rapide domaine.",
    impactMetier: "Rapports veille 4/an, Évolutions 20 identifiées, Recommandations intégrées 50%, Stratégie IA actualisée, Anticipation vs obsolescence",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 3,
    target: 4,
    raci: { responsible: "Directeur TI BZ", accountable: "Direction", consulted: "Experts", informed: "Équipe TI" },
    livrables: ["Rapports veille trimestriels", "Notes stratégiques", "Comparatifs"],
    livrablesVisuels: [
      {
        nom: "Rapport Veille IA T4 2024",
        type: "Rapport",
        description: "Document 10 pages: 4 évolutions GPT-4 Turbo/Azure AI/agents/multimodalité, applicabilité, recommandations 2 adoptées",
        imageUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "Rapports produits: 4/an | % recommandations intégrées: 50%"
  },
  {
    id: "P7-T07",
    nom: "Backlog développement AI (CODEHUB)",
    pilier: 7,
    description: "Structurer et prioriser efforts développement AI",
    priorite: "Haute",
    statut: "Fait",
    asWas: 0,
    asIs: 4,
    target: 5,
    raci: { responsible: "Chef prog BZ", accountable: "Directeur TI", consulted: "Équipe prog", informed: "Direction" },
    livrables: ["Backlog CODEHUB", "Priorisation tâches", "MAJ mensuelles"],
    kpi: "% tâches AI documentées: 100% | Délai traitement: 3 semaines"
  },
  {
    id: "P7-T08",
    nom: "Standardiser environnements CI/CD DevSecOps",
    pilier: 7,
    description: "Fiabiliser et accélérer développements AI",
    descriptionDetaillee: "Pipelines CI/CD (Continuous Integration/Deployment) + DevSecOps automatisent tests/sécurité/déploiements projets IA accélérant time-to-market réduisant erreurs manuelles. CI: code committé GitHub/Azure DevOps trigger automatiquement build + tests unitaires (pytest Python), tests intégration (API endpoints), tests performance (latence modèles ML <500ms), linting code qualité (Black, Flake8). CD: tests passent → déploiement automatique environnements staging puis production (approval gate). DevSecOps: scans sécurité automatiques (Snyk vulnérabilités dépendances, SAST analyse code statique, secrets detection). Bénéfices: délais déploiements 2 semaines → 2 jours, qualité code améliorée (bugs détectés pré-production), sécurité intégrée (shift-left). Target: 90% projets IA via CI/CD, 92%+ tests automatisés succès.",
    casUsage: "TechnoSolutions CI/CD DevSecOps IA T2-T4 2024. Plateformeavre Azure DevOps pipelines configurés 3 projets IA (automatisation rapports, prédiction inventaire, analyse feedback). Pipeline standard: 1) commit code → 2) build container Docker → 3) tests unitaires 145 tests automatisés → 4) tests intégration API → 5) scan sécurité Snyk → 6) déploiement staging validation manuelle → 7) approval direction → 8) déploiement production. Métriques: 60% projets IA via CI/CD (3/5 projets, cible 90% = 2 projets legacy manuels migration T1 2025), tests automatisés succès 92% (cible atteinte). Bénéfices: délais déploiements automatisation rapports 12j manuels → 3j CI/CD (-75%), bugs production -68%, sécurité vulnérabilités détectées pré-production 8 corrigées (vs 0 détection manuelle).",
    impactMetier: "Projets IA CI/CD 60% → 90%, Tests auto succès 92%, Délais déploiements -75%, Bugs production -68%, Sécurité intégrée",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 1,
    asIs: 2,
    target: 4,
    raci: { responsible: "Équipe prog BZ", accountable: "Directeur TI", consulted: "DevOps", informed: "Support" },
    livrables: ["Pipelines CI/CD", "Documentation DevSecOps", "Audits conformité"],
    livrablesVisuels: [
      {
        nom: "Pipeline CI/CD IA Azure DevOps",
        type: "Processus",
        description: "Diagramme: 8 étapes commit→build→tests→security→staging→approval→production, durée 3j vs 12j manuel",
        imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% projets AI via CI/CD: 60% (cible: 90%) | % tests auto réussis: 92%"
  },
  {
    id: "P7-T09",
    nom: "Documentation code et API intégrations AI",
    pilier: 7,
    description: "Assurer pérennité et maintenabilité",
    descriptionDetaillee: "Documentation code IA = assurance maintenabilité long-terme projets complexes (modèles ML = boîtes noires nécessitant explications). Standards: 1) DOCSTRINGS Python fonctions/classes (params, returns, exemples usage). 2) README modules architecture (design decisions, dépendances, deployment). 3) API référentiel endpoints REST documentation Swagger/OpenAPI (requêtes/réponses formats, authentification, rate limits). 4) ARCHITECTURE DECISIONS RECORDS (ADR) justifiant choix techniques (pourquoi GPT-4 vs Claude?, pourquoi Azure vs AWS?). 5) GUIDES intégration développeurs externes (onboarding nouveaux devs 2-3j vs 2 semaines sans doc). 6) JUPYTER notebooks tutoriels reproduisant expérimentations. Target: 95% modules IA documentés, score qualité 4/5+ (reviews pairs).",
    casUsage: "TechnoSolutions documentation IA 2024. Modules IA: 5 projets développés (chatbot, rapports, inventaire, feedback, anomalies). Documentation actuelle: 75% modules documentés (4/5 = projet anomalies manque doc complète cible T1 2025). Qualité: score moyen 3.8/5 reviews code (gaps = README architecture incomplets 2 projets, ADR absents justifiant choix modèles). Actions amélioration: template documentation standardisé obligatoire nouveaux projets, refactorisation documentation legacy 2 projets 8j effort, workshops documentation best practices équipe prog. Bénéfices: onboarding nouveau dev T3 2024 durée 4j avec doc (vs 12j estimation sans doc = gain 67%), maintenance projets facilitée (corrections bugs 2x plus rapides code documenté).",
    impactMetier: "Modules documentés 75% → 95%, Qualité doc 3.8 → 4.2/5, Onboarding devs -67% temps, Maintenance facilitée 2x rapide",
    priorite: "Moyenne",
    statut: "En cours",
    asWas: 2,
    asIs: 3,
    target: 4,
    raci: { responsible: "Équipe prog BZ", accountable: "Chef prog", consulted: "Développeurs", informed: "Support" },
    livrables: ["Documentation code", "Référentiel API", "Guide intégration"],
    livrablesVisuels: [
      {
        nom: "Documentation API IA Swagger",
        type: "Document",
        description: "Référentiel OpenAPI: 12 endpoints REST automatisation rapports, authentification OAuth, exemples requêtes",
        imageUrl: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&auto=format&fit=crop"
      },
      {
        nom: "Guide Intégration Développeurs",
        type: "Document",
        description: "Manuel 25 pages: architecture projets IA, setup environnements, ADR, tutoriels Jupyter, best practices",
        imageUrl: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&auto=format&fit=crop"
      }
    ],
    kpi: "% modules AI documentés: 75% (cible: 95%) | Score qualité: 3.8/5"
  }
];

// KPI Globaux par Pilier
export const kpiGlobaux = {
  pilier1: {
    principal: "Score satisfaction directions: 78%",
    secondaire: "% projets alignés priorités: 75%",
    tendance: "↗️ +12% vs trimestre précédent"
  },
  pilier2: {
    principal: "% actifs inventoriés: 92%",
    secondaire: "% postes conformes sécu: 89%",
    tendance: "↗️ +8% vs trimestre précédent"
  },
  pilier3: {
    principal: "% projets dans budget/délais: 78%",
    secondaire: "ROI moyen projets: +165%",
    tendance: "↗️ +5% vs trimestre précédent"
  },
  pilier4: {
    principal: "Temps résolution tickets: 4.2h",
    secondaire: "Disponibilité systèmes: 99.7%",
    tendance: "↗️ Amélioration continue"
  },
  pilier5: {
    principal: "Score maturité cyber NIST: 68%",
    secondaire: "% systèmes patchés <7j: 94%",
    tendance: "↗️ +15% vs évaluation initiale"
  },
  pilier6: {
    principal: "% budget innovation: 5%",
    secondaire: "Processus automatisés: 12",
    tendance: "→ Stabilisation"
  },
  pilier7: {
    principal: "Cas usage AI testés: 5/8",
    secondaire: "POC AI convertis: 67%",
    tendance: "↗️ Accélération rapide"
  }
};

// Roadmap trimestrielle
export const roadmap = [
  {
    trimestre: "T4 2024",
    priorites: [
      "Finaliser déploiement IAM/SSO (P2-T05)",
      "Test pénétration annuel (P5-T06)",
      "POC AI chatbot support (P7-T04)",
      "Comité TI Q4 et revue annuelle"
    ],
    budget: "125,000$",
    tactiquesActives: 6
  },
  {
    trimestre: "T1 2025",
    priorites: [
      "Compléter formation cybersécurité (P5-T03)",
      "Lancer ateliers innovation (P6-T05)",
      "Déployer EDR sur 100% endpoints (P5-T07)",
      "Roadmap annuel 2025"
    ],
    budget: "145,000$",
    tactiquesActives: 8
  },
  {
    trimestre: "T2 2025",
    priorites: [
      "Audit complet conformité Loi 25 (P2-T10)",
      "Automatiser 5 processus manuels (P6-T03)",
      "POC AI analyse prédictive (P7-T04)",
      "Comité TI Q2"
    ],
    budget: "135,000$",
    tactiquesActives: 7
  },
  {
    trimestre: "T3-T4 2025",
    priorites: [
      "Atteindre 85% conformité NIST (P5-T04)",
      "Déployer Innovation Funnel (P6-T08)",
      "Formation IA 90% équipes (P7-T05)",
      "Revue annuelle complète"
    ],
    budget: "180,000$",
    tactiquesActives: 10
  }
];

// Calculer score moyen pilier à partir tactiques
export const calculerScorePilier = (numeroPilier: number, type: 'asWas' | 'asIs' | 'target'): number => {
  const tactiquesPilier = tactiques.filter(t => t.pilier === numeroPilier);
  const somme = tactiquesPilier.reduce((acc, t) => acc + t[type], 0);
  return Math.round((somme / tactiquesPilier.length) * 10) / 10;
};

